/**
 * PageEditor.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from './ToastContainer';
import { useAuth } from '../context/AuthContext';
import EditableContentDual from './EditableContentDual';
import ProductPickerModal from './ProductPickerModal';
import HelpPopup from './HelpPopup';
import TuneFieldSelectWithAdd from './TuneFieldSelectWithAdd';
import RelatedContentPreview from './RelatedContentPreview';
import PagePreviewModal from './PagePreviewModal';
import { DEFAULT_TUNE_METADATA_FIELD_OPTIONS } from '../constants/tuneMetadataFieldOptions';
import { fetchTuneSiteOptions } from '../utils/tuneSiteOptions';
import { getIcon } from '../utils/icons';
import {
  normalizeTunePageMetadataAssetIds,
  syncTuneAudioMetadataFields,
} from '../utils/tunePageMedia';
import { normalizeTimeSignature } from '../constants/tuneTimeSignatures';
import {
  buildArticleBrowseItemFromAdminPageRow,
  articleBrowseItemMatchesSearchTerm,
} from '../utils/articleBrowseSearch';
import {
  queryMatchesSearchText,
  productRowSearchHaystack,
  tuneRowSearchHaystack,
} from '../utils/siteSearch';
import { mergeTagLists, parseTagList, suggestPageTags } from '../utils/autoPopulateTags';
import './PageEditor.css';

/** Defaults align with PageViewer / tune-sections API */
const DEFAULT_RELATED_SECTION_LIMITS = { products: 24, articles: 8, tunes: 12, curated: 24 };

/** Parse linked product IDs from page metadata (article + tune fields). */
function parseProductIdsFromMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return [];
  const raw = [];
  const add = (v) => {
    if (v === null || v === undefined || v === '') return;
    if (typeof v === 'number' && Number.isFinite(v)) {
      raw.push(Math.trunc(v));
      return;
    }
    const s = String(v).trim();
    if (!s) return;
    s.split(',').forEach((part) => {
      const t = part.trim();
      if (t && !Number.isNaN(Number(t))) raw.push(parseInt(t, 10));
    });
  };
  add(metadata.product_ids);
  add(metadata.product_id);
  add(metadata.productId);
  add(metadata.relatedProductId);
  const seen = new Set();
  return raw.filter((id) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** SPA targets for Curated Picks (pages not listed in content API) */
const CURATED_NAV_ROUTES = [
  { href: '/tunes', title: 'Tunes library', searchText: 'tunes abc notation browse music fiddle' },
  { href: '/articles', title: 'Articles', searchText: 'articles blog read stories' },
  { href: '/shop', title: 'Shop', searchText: 'shop store products buy cds books recordings' },
  { href: '/information', title: 'Information', searchText: 'information info help tools resources' },
  { href: '/faq', title: 'FAQ', searchText: 'faq questions answers help' },
  { href: '/about', title: 'About', searchText: 'about cranford publications' },
  { href: '/contact', title: 'Contact', searchText: 'contact email reach' },
  { href: '/developer', title: 'Developer', searchText: 'developer api tools technical' },
  { href: '/', title: 'Home', searchText: 'home landing start' },
];

function dedupePagesByPageId(pages) {
  const m = new Map();
  for (const p of pages || []) {
    const id = p?.page_id;
    if (!id) continue;
    const prev = m.get(id);
    if (!prev) {
      m.set(id, p);
      continue;
    }
    const t = (p.title || '').trim();
    const tp = (prev.title || '').trim();
    if (t.length > tp.length) m.set(id, p);
  }
  return [...m.values()];
}

function stripHtmlLite(s) {
  return String(s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function curatedPageSearchScore(p, lq) {
  const id = (p.page_id || '').toLowerCase();
  const t = (p.title || '').toLowerCase();
  const d = stripHtmlLite(p.description || '').toLowerCase().slice(0, 500);
  if (id === lq) return 100;
  if (id.startsWith(lq)) return 85;
  if (t.startsWith(lq)) return 70;
  if (t.includes(lq)) return 50;
  if (d.includes(lq)) return 35;
  if (id.includes(lq)) return 30;
  return 0;
}

/** Display title for an ABC-Tune asset (matches Tunes.js metadata pattern). */
function parseAbcAssetTitle(asset) {
  if (!asset) return '';
  try {
    if (asset.description) {
      const m = asset.description.match(/\[ABC_METADATA\](.*?)\[\/ABC_METADATA\]/s);
      if (m) {
        const meta = JSON.parse(m[1]);
        if (meta.tune_title) return String(meta.tune_title);
      }
    }
  } catch (_) {
    /* ignore */
  }
  return asset.original_filename || asset.filename || (asset.id != null ? `ABC #${asset.id}` : '');
}

function abcAssetSearchBlob(asset) {
  const title = parseAbcAssetTitle(asset);
  const desc = stripHtmlLite(
    String(asset.description || '').replace(/\[ABC_METADATA\].*?\[\/ABC_METADATA\]/s, ' ')
  ).toLowerCase();
  return `${asset.id} ${title} ${asset.filename || ''} ${asset.original_filename || ''} ${asset.tags || ''} ${desc}`
    .toLowerCase();
}

/** e.g. /tune-asset/1850, full URL, or bare id (3+ digits). */
function parseTuneAssetIdFromInput(raw) {
  const s = String(raw || '').trim();
  const m = s.match(/tune-asset\/(\d+)/i);
  if (m) return parseInt(m[1], 10);
  if (/^\d{3,}$/.test(s)) return parseInt(s, 10);
  return null;
}

/**
 * Extract ALL notation and audio asset IDs baked into the page HTML.
 * Returns { notationIds: number[], audioIds: number[] }
 *
 * Key heuristic: if the same ID appears in BOTH a thumbnail URL and a download URL
 * it is almost certainly an audio file that was incorrectly placed in the notation
 * <img> tag on legacy pages. In that case keep it only in audioIds.
 */
function extractAssetIdsFromHtml(html) {
  if (!html) return { notationIds: [], audioIds: [] };

  // Only scan the tune-notation block for thumbnail IDs (avoids picking up
  // product cover images or other thumbnails elsewhere on the page)
  const notationBlock = html.match(/<div[^>]*\btune-notation\b[^>]*>[\s\S]*?<\/div>/i);
  const rawNotationIds = notationBlock
    ? [...notationBlock[0].matchAll(/assets\/(\d+)\/thumbnail/gi)].map(m => parseInt(m[1], 10))
    : [...html.matchAll(/api\/assets\/(\d+)\/thumbnail/gi)].map(m => parseInt(m[1], 10));

  // Only scan the tune-audio / abc-audio block for download IDs
  const audioBlock = html.match(/<div[^>]*\b(?:tune-audio|abc-audio)\b[^>]*>[\s\S]*?<\/div>/i);
  const rawAudioIds = audioBlock
    ? [...audioBlock[0].matchAll(/assets\/(\d+)\/download/gi)].map(m => parseInt(m[1], 10))
    : [...html.matchAll(/api\/assets\/(\d+)\/download/gi)].map(m => parseInt(m[1], 10));

  const audioSet = new Set(rawAudioIds);

  // Remove from notationIds any ID that also appears in audioIds —
  // those are audio files incorrectly referenced as notation images
  const notationIds = [...new Set(rawNotationIds)].filter(id => !audioSet.has(id));
  const audioIds    = [...new Set(rawAudioIds)];

  return { notationIds, audioIds };
}

/**
 * Searchable asset picker — picks a single asset.
 * fileType: 'image' | 'music'
 * onSelect: called with the asset object when user picks one from the dropdown.
 * Used standalone or as the "add" row inside MultiAssetList.
 */
function AssetSearchInput({ fileType, placeholder, onSelect }) {
  const [query,     setQuery]     = useState('');
  const [results,   setResults]   = useState([]);
  const [open,      setOpen]      = useState(false);
  const [searching, setSearching] = useState(false);
  const containerRef = useRef(null);

  // Debounce search
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ search: query, limit: 12 });
        if (fileType) params.set('file_type', fileType);
        const r = await fetch(`/api/assets?${params}`);
        const data = await r.json();
        setResults(Array.isArray(data.assets) ? data.assets : []);
      } catch { setResults([]); }
      setSearching(false);
    }, 280);
    return () => clearTimeout(t);
  }, [query, fileType]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (asset) => {
    if (onSelect) onSelect(asset);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="asset-search-input" ref={containerRef}>
      <div className="asset-search-box">
        <input
          type="text"
          className="form-control"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => query && setOpen(true)}
          placeholder={placeholder || 'Search and add…'}
        />
        {searching && <span className="asset-search-spinner">⏳</span>}
      </div>
      {open && results.length > 0 && (
        <ul className="asset-search-dropdown">
          {results.map(a => {
            const isImg = (a.file_type === 'image') || (a.mime_type || '').startsWith('image/');
            const name = a.original_filename || a.filename || `#${a.id}`;
            return (
              <li key={a.id} onMouseDown={() => pick(a)} className="asset-search-option">
                {isImg
                  ? <img src={`/api/assets/${a.id}/thumbnail`} alt="" className="asset-search-thumb" />
                  : <span className="asset-search-icon">🎵</span>}
                <span className="asset-search-option-name">{name}</span>
                <span className="asset-search-option-id">#{a.id}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Single asset chip — shows thumbnail/icon, name, ID and a remove button. */
function AssetChip({ assetId, onRemove, isImage: isImageHint }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!assetId) return;
    fetch(`/api/assets/${assetId}`)
      .then(r => r.ok ? r.json() : null)
      .then(a => {
        if (!a) return;
        const isImg = isImageHint ?? ((a.file_type === 'image') || (a.mime_type || '').startsWith('image/'));
        setInfo({ name: a.original_filename || a.filename || `Asset #${assetId}`, isImg });
      })
      .catch(() => {});
  }, [assetId, isImageHint]);

  return (
    <div className="asset-chip">
      {info?.isImg
        ? <img src={`/api/assets/${assetId}/thumbnail`} alt="" className="asset-search-thumb" />
        : <span className="asset-search-icon">🎵</span>}
      <span className="asset-chip-name">{info ? info.name : `#${assetId}`}</span>
      <span className="asset-chip-id">#{assetId}</span>
      <button type="button" className="asset-search-clear" onClick={onRemove} title="Remove">✕</button>
    </div>
  );
}

/**
 * Multi-asset list — manages an ordered array of asset IDs.
 * ids: number[]   onChange: (ids: number[]) => void
 */
function MultiAssetList({ ids, onChange, fileType, addPlaceholder, isImage }) {
  const [idInput, setIdInput] = useState('');

  const addById = () => {
    const n = parseInt(idInput, 10);
    if (!n || ids.includes(n)) { setIdInput(''); return; }
    onChange([...ids, n]);
    setIdInput('');
  };

  const addFromSearch = (asset) => {
    if (ids.includes(asset.id)) return;
    onChange([...ids, asset.id]);
  };

  const remove = (id) => onChange(ids.filter(x => x !== id));

  return (
    <div className="multi-asset-list">
      {/* Existing chips */}
      {ids.length > 0 && (
        <div className="multi-asset-chips">
          {ids.map(id => (
            <AssetChip key={id} assetId={id} onRemove={() => remove(id)} isImage={isImage} />
          ))}
        </div>
      )}

      {/* Add row: direct ID entry OR search */}
      <div className="multi-asset-add-row">
        <input
          type="number"
          className="form-control asset-id-field"
          value={idInput}
          onChange={e => setIdInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addById()}
          placeholder="ID"
          min="1"
          style={{ width: '78px', flexShrink: 0 }}
        />
        <button type="button" className="btn btn-sm btn-secondary" onClick={addById} style={{ whiteSpace: 'nowrap' }}>+ Add</button>
        <div style={{ flex: 1, position: 'relative' }}>
          <AssetSearchInput fileType={fileType} placeholder={addPlaceholder || 'Search and add…'} onSelect={addFromSearch} />
        </div>
      </div>

      {ids.length === 0 && (
        <p className="multi-asset-empty">No assets added — search or enter an ID above.</p>
      )}
    </div>
  );
}

/** Disambiguate curated picks (page vs product vs ABC asset ids can overlap). */
function curatedRowKey(entry) {
  if (!entry) return '';
  if (entry.type === 'route') return `route:${entry.href}`;
  if (entry.type === 'product') return `product:${entry.id}`;
  if (entry.type === 'tune-asset') return `tune-asset:${entry.id}`;
  return `page:${entry.id}`;
}

const HOME_ANNOUNCEMENT_PAGE_TYPES = new Set(['article', 'news', 'review', 'biography', 'recording']);

const PageEditor = () => {
  const { showError, showSuccess } = useToast();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [pageData, setPageData] = useState({
    pageId: '',
    title: '',
    description: '',
    type: 'general',
    featured: false,
    home_announcement: false,
    is_hidden: false,
    html: '',
    metadata: {}
  });
  const [loading, setLoading] = useState(true);
  const [isNewPage, setIsNewPage] = useState(false);
  const [isLegacyPage, setIsLegacyPage] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [allPages, setAllPages] = useState([]);
  /** ABC interactive tunes (/tune-asset/:id) — not listed on /api/content/pages */
  const [abcTuneAssets, setAbcTuneAssets] = useState([]);
  const [versionHistory, setVersionHistory] = useState([]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [previewVersionId, setPreviewVersionId] = useState(null);
  const [showVersionPreview, setShowVersionPreview] = useState(false);
  const [tuneFieldOptions, setTuneFieldOptions] = useState(DEFAULT_TUNE_METADATA_FIELD_OPTIONS);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showArticleProductPicker, setShowArticleProductPicker] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productIdsInput, setProductIdsInput] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [pickerQuery, setPickerQuery] = useState({ articles: '', tunes: '', products: '', curated: '' });
  const [pickerResults, setPickerResults] = useState({ articles: [], tunes: [], products: [], curated: [] });
  const [editorKey, setEditorKey] = useState(0); // Force re-render of editor when content changes externally
  const [pageIdInput, setPageIdInput] = useState('');   // editable page ID for new pages
  const [pageIdError, setPageIdError] = useState('');   // slug validation message
  const [renamePageIdInput, setRenamePageIdInput] = useState('');
  const [renamePageIdError, setRenamePageIdError] = useState('');
  const [renamePageIdBusy, setRenamePageIdBusy] = useState(false);
  const [templateBanner, setTemplateBanner] = useState(''); // set when tune template is auto-applied

  const [articleLayoutChoices, setArticleLayoutChoices] = useState([
    { layout_key: 'default', name: 'Standard Layout', description: 'Clean modern sidebar layout' },
    { layout_key: 'violin', name: 'Violin Article (Classic)', description: 'Serif / drop cap' },
    { layout_key: 'editorial', name: 'Editorial (Magazine)', description: 'Magazine-style read' }
  ]);

  // Predefined page types with descriptions
  const predefinedPageTypes = [
    { value: 'general', label: 'General Page', description: 'Standard content page' },
    { value: 'product-detail', label: 'Product Detail', description: 'Detailed product information page' },
    { value: 'tune-notation', label: 'Tune Notation', description: 'Musical notation and sheet music' },
    { value: 'abc-notation', label: 'ABC Notation', description: 'ABC musical notation format' },
    { value: 'book-index', label: 'Book Index', description: 'Book tune index pages' },
    { value: 'tutorial', label: 'Tutorial', description: 'Educational content and guides' },
    { value: 'news', label: 'News Article', description: 'News and announcements' },
    { value: 'article', label: 'Article', description: 'Historical articles and cultural content' },
    { value: 'biography', label: 'Biography', description: 'Artist or musician biographies' },
    { value: 'recording', label: 'Recording', description: 'Album or recording pages' },
    { value: 'review', label: 'Review', description: 'Product or recording reviews' },
    { value: 'about', label: 'About Page', description: 'About us or company information' }
  ];

  // Get all unique page types from existing pages
  const existingPageTypes = [...new Set(allPages.map(p => p.page_type).filter(Boolean))];

  // Combine predefined and existing types
  const allPageTypes = [
    ...predefinedPageTypes,
    // Add any existing types that aren't in predefined list
    ...existingPageTypes
      .filter(type => !predefinedPageTypes.some(pt => pt.value === type))
      .map(type => ({
        value: type,
        label: type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        description: 'Custom page type'
      }))
  ];

  useEffect(() => {
    initializePage();
    loadAllPages();
    (async () => {
      try {
        const r = await fetch('/api/assets?category=ABC-Tune&is_active=1&limit=2000');
        if (!r.ok) return;
        const data = await r.json();
        setAbcTuneAssets(Array.isArray(data.assets) ? data.assets : []);
      } catch (e) {
        console.error('ABC tune assets list:', e);
      }
    })();
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTuneSiteOptions().then((opts) => {
      if (opts?.tuneMetadataFieldOptions) {
        setTuneFieldOptions(opts.tuneMetadataFieldOptions);
      }
    });
  }, []);

  useEffect(() => {
    if (!loading && pageData.pageId && !isNewPage) {
      loadVersionHistory();
    }
  }, [loading, pageData.pageId, isNewPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetch('/api/content/article-layouts')
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (Array.isArray(rows) && rows.length > 0) setArticleLayoutChoices(rows);
      })
      .catch(() => {});
  }, []);

  // Auto-trigger metadata population when content is loaded and tags are empty
  useEffect(() => {
    if (!loading && pageData.html && pageData.html.length > 50) {
      // Handle tags as both string and array
      let tagsValue = '';
      if (pageData.metadata.tags) {
        if (Array.isArray(pageData.metadata.tags)) {
          tagsValue = pageData.metadata.tags.join(', ');
        } else if (typeof pageData.metadata.tags === 'string') {
          tagsValue = pageData.metadata.tags.trim();
        }
      }
      const hasTags = tagsValue !== '';

      // Convert array tags to string format for editing
      if (Array.isArray(pageData.metadata.tags)) {
        setPageData(prev => ({
          ...prev,
          metadata: { ...prev.metadata, tags: tagsValue }
        }));
      }

      // If this is a new page or tags are empty, suggest auto-population
      if (!hasTags && isNewPage) {
        // Auto-populate after a brief delay to let user see the content first
        const timer = setTimeout(() => {
          autoPopulateMetadata();
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, pageData.html, isNewPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch products for product picker
  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products');
      if (response.ok) {
        const data = await response.json();
        const productsList = Array.isArray(data) ? data : (data.products || []);
        setProducts(productsList);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Initialize products on mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Sync linked product pickers when page loads or products catalog arrives
  useEffect(() => {
    if (!pageData.metadata || products.length === 0 || loading) return;
    const ids = parseProductIdsFromMetadata(pageData.metadata);
    setSelectedProductIds(ids);
    setProductIdsInput(ids.join(', '));
    setSelectedProducts(products.filter((p) => ids.includes(p.id)));
  }, [loading, pageData.pageId, products]); // eslint-disable-line react-hooks/exhaustive-deps


  // Load all pages to get existing page types
  const loadAllPages = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const cacheBuster = new Date().getTime();
      const response = await fetch(`/api/content/pages?_=${cacheBuster}`, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllPages(data);
      }
    } catch (error) {
      console.error('Error loading pages:', error);
    }
  };

  // Typed search for the per-section pickers
  const handleTypedSearch = (pickerKey, query) => {
    setPickerQuery(prev => ({ ...prev, [pickerKey]: query }));
    const trimmedQ = (query || '').trim();
    const urlAssetId = pickerKey === 'curated' ? parseTuneAssetIdFromInput(trimmedQ) : null;
    if (!trimmedQ || (trimmedQ.length < 2 && urlAssetId == null)) {
      setPickerResults(prev => ({ ...prev, [pickerKey]: [] }));
      return;
    }
    const lq = trimmedQ.toLowerCase();
    const rc = pageData.metadata?.related_content || {};

    if (pickerKey === 'products') {
      const existing = new Set((rc.products || []).map(p => String(p.id)));
      const res = products
        .filter(p => !existing.has(String(p.id)) && queryMatchesSearchText(productRowSearchHaystack(p), trimmedQ, { title: p.name }))
        .slice(0, 8)
        .map(p => ({ id: p.id, label: p.name, sub: `$${(p.price_cents/100).toFixed(2)}`, _key: String(p.id) }));
      setPickerResults(prev => ({ ...prev, products: res }));
    } else if (pickerKey === 'articles') {
      const existing = new Set((rc.articles || []).map(a => a.id));
      const articleTypes = ['article', 'biography', 'general', 'book-index'];
      const candidates = allPages.filter(p => !existing.has(p.page_id) && p.page_id !== pageData.pageId
        && articleTypes.includes(p.page_type));
      const res = candidates
        .filter((p) => {
          const item = buildArticleBrowseItemFromAdminPageRow(p);
          return articleBrowseItemMatchesSearchTerm(item, trimmedQ);
        })
        .sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase()))
        .slice(0, 8)
        .map(p => ({ id: p.page_id, label: p.title || p.page_id, sub: p.page_type, _key: p.page_id }));
      setPickerResults(prev => ({ ...prev, articles: res }));
    } else if (pickerKey === 'tunes') {
      const existing = new Set((rc.tunes || []).map(t => t.id));
      const TUNE_PAGE_TYPES = new Set(['tune-notation', 'abc-notation', 'book-index', 'cd-index']);
      const TYPE_LABELS = { 'book-index': 'Book Index', 'cd-index': 'CD Index', 'tune-notation': 'Tune', 'abc-notation': 'ABC Tune' };
      const res = allPages
        .filter(p => !existing.has(p.page_id) && p.page_id !== pageData.pageId
          && TUNE_PAGE_TYPES.has(p.page_type)
          && queryMatchesSearchText(tuneRowSearchHaystack(p), trimmedQ))
        .slice(0, 12)
        .map(p => ({ id: p.page_id, label: p.title || p.page_id, sub: TYPE_LABELS[p.page_type] || p.page_type, _key: p.page_id }));
      setPickerResults(prev => ({ ...prev, tunes: res }));
    } else if (pickerKey === 'curated') {
      const curatedExisting = rc.curated || [];
      const taken = new Set(curatedExisting.map(curatedRowKey));

      const navRes = CURATED_NAV_ROUTES
        .filter((r) => !taken.has(`route:${r.href}`) && queryMatchesSearchText(
          `${r.title}\n${r.href}\n${r.searchText || ''}`.toLowerCase(),
          trimmedQ,
        ))
        .map((r) => ({
          id: r.href,
          label: r.title,
          sub: 'Site link',
          itemType: 'route',
          href: r.href,
          _key: `route:${r.href}`,
        }));

      const pagesUnique = dedupePagesByPageId(allPages);
      const pageRes = pagesUnique
        .filter((p) => p.page_id && p.page_id !== pageData.pageId && !taken.has(`page:${p.page_id}`))
        .filter((p) => queryMatchesSearchText(tuneRowSearchHaystack(p), trimmedQ))
        .map((p) => ({ p, score: curatedPageSearchScore(p, lq) }))
        .sort((a, b) => b.score - a.score || (a.p.title || '').localeCompare(b.p.title || ''))
        .slice(0, 24)
        .map(({ p }) => {
          const pt = p.page_type || 'page';
          const isTune = pt === 'tune-notation' || pt === 'abc-notation';
          return {
            id: p.page_id,
            label: p.title || p.page_id,
            sub: pt,
            itemType: isTune ? 'tune' : 'article',
            _key: p.page_id,
          };
        });

      let assetRes = abcTuneAssets
        .filter((a) => a && a.id != null && !taken.has(`tune-asset:${a.id}`) && queryMatchesSearchText(abcAssetSearchBlob(a), trimmedQ))
        .slice(0, 20)
        .map((a) => ({
          id: a.id,
          tuneAssetId: a.id,
          label: parseAbcAssetTitle(a),
          sub: 'ABC player',
          itemType: 'tune-asset',
          _key: `abc:${a.id}`,
        }));

      if (urlAssetId != null && !Number.isNaN(urlAssetId) && !taken.has(`tune-asset:${urlAssetId}`)) {
        const local = abcTuneAssets.find((a) => Number(a.id) === urlAssetId);
        if (local) {
          const row = {
            id: local.id,
            tuneAssetId: local.id,
            label: parseAbcAssetTitle(local),
            sub: 'ABC player (from link / id)',
            itemType: 'tune-asset',
            _key: `abc:${local.id}`,
          };
          if (!assetRes.some((r) => Number(r.tuneAssetId) === urlAssetId)) {
            assetRes = [row, ...assetRes];
          }
        } else {
          fetch(`/api/assets/${urlAssetId}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
              if (!data || data.id == null) return;
              const title = parseAbcAssetTitle(data);
              const row = {
                id: data.id,
                tuneAssetId: data.id,
                label: title,
                sub: 'ABC player',
                itemType: 'tune-asset',
                _key: `abc:${data.id}`,
              };
              setPickerResults((prev) => {
                const list = prev.curated || [];
                if (list.some((x) => x._key === row._key)) return prev;
                return { ...prev, curated: [row, ...list].slice(0, 50) };
              });
            })
            .catch(() => {});
        }
      }

      const prodRes = products
        .filter((p) => !taken.has(`product:${p.id}`) && queryMatchesSearchText(productRowSearchHaystack(p), trimmedQ, { title: p.name }))
        .slice(0, 10)
        .map((p) => ({ id: p.id, label: p.name, sub: 'product', itemType: 'product', _key: `prod:${p.id}` }));

      setPickerResults((prev) => ({
        ...prev,
        curated: [...navRes, ...assetRes, ...pageRes, ...prodRes].slice(0, 50),
      }));
    }
  };

  const addTypedItem = (pickerKey, item) => {
    const rc = pageData.metadata?.related_content || {};
    let updated = { ...rc };
    if (pickerKey === 'products') {
      const existing = rc.products || [];
      if (existing.some(e => String(e.id) === String(item.id))) return;
      updated.products = [...existing, { id: item.id }];
    } else if (pickerKey === 'articles') {
      const existing = rc.articles || [];
      if (existing.some(e => e.id === item.id)) return;
      updated.articles = [...existing, { id: item.id }];
    } else if (pickerKey === 'tunes') {
      const existing = rc.tunes || [];
      if (existing.some(e => e.id === item.id)) return;
      updated.tunes = [...existing, { id: item.id }];
    } else if (pickerKey === 'curated') {
      const existing = rc.curated || [];
      if (item.itemType === 'route' && item.href) {
        if (existing.some((e) => e.type === 'route' && e.href === item.href)) return;
        updated.curated = [...existing, { type: 'route', href: item.href, title: item.label || item.title || '' }];
      } else if (item.itemType === 'tune-asset') {
        const aid = item.tuneAssetId ?? item.id;
        if (existing.some((e) => e.type === 'tune-asset' && String(e.id) === String(aid))) return;
        updated.curated = [...existing, { type: 'tune-asset', id: aid, title: item.label || '' }];
      } else {
        if (existing.some((e) => String(e.id) === String(item.id) && (e.type || 'article') === (item.itemType || 'article'))) return;
        updated.curated = [...existing, { id: item.id, type: item.itemType || 'article' }];
      }
    }

    // Picking a page/product again means it should show — drop from excluded list.
    const addId = pickerKey === 'products'
      ? String(item.id)
      : item.itemType === 'route'
        ? item.href
        : item.itemType === 'tune-asset'
          ? `tune-asset:${item.tuneAssetId ?? item.id}`
          : item.id;
    if (addId != null && String(addId).length) {
      const ex = (updated.excluded || []).filter((e) => String(e.id) !== String(addId));
      if (ex.length !== (updated.excluded || []).length) updated.excluded = ex;
    }

    // Raise section cap so new picks are not hidden on the live page (viewer caps by section_limits).
    const limKey = pickerKey;
    if (limKey === 'products' || limKey === 'articles' || limKey === 'tunes' || limKey === 'curated') {
      const listLen =
        limKey === 'products' ? (updated.products || []).length
          : limKey === 'articles' ? (updated.articles || []).length
            : limKey === 'tunes' ? (updated.tunes || []).length
              : (updated.curated || []).length;
      const prevLim = { ...(rc.section_limits || {}) };
      const raw = prevLim[limKey];
      const n = parseInt(raw, 10);
      const defLim = DEFAULT_RELATED_SECTION_LIMITS[limKey] ?? 8;
      const effective = Number.isFinite(n) && n >= 1 ? n : defLim;
      if (listLen > effective) {
        updated.section_limits = { ...prevLim, [limKey]: Math.min(100, listLen) };
      }
    }

    setPageData(prev => ({ ...prev, metadata: { ...prev.metadata, related_content: updated } }));
    setPickerQuery(prev => ({ ...prev, [pickerKey]: '' }));
    setPickerResults(prev => ({ ...prev, [pickerKey]: [] }));
  };

  const removeTypedItem = (pickerKey, itemId) => {
    const rc = pageData.metadata?.related_content || {};
    let updated = { ...rc };
    if (pickerKey === 'products') updated.products = (rc.products || []).filter(p => String(p.id) !== String(itemId));
    else if (pickerKey === 'articles') updated.articles = (rc.articles || []).filter(a => a.id !== itemId);
    else if (pickerKey === 'tunes') updated.tunes = (rc.tunes || []).filter(t => t.id !== itemId);
    else if (pickerKey === 'curated') {
      updated.curated = (rc.curated || []).filter((i) => {
        if (i.type === 'route') return String(i.href) !== String(itemId);
        return String(i.id) !== String(itemId);
      });
    }
    setPageData(prev => ({ ...prev, metadata: { ...prev.metadata, related_content: updated } }));
  };

  const removeCuratedPick = (entry) => {
    const rc = pageData.metadata?.related_content || {};
    const same = (a, b) => {
      if (a.type === 'route' && b.type === 'route') return a.href === b.href;
      if (a.type === 'tune-asset' && b.type === 'tune-asset') return String(a.id) === String(b.id);
      if (a.type === 'product' && b.type === 'product') return String(a.id) === String(b.id);
      if (a.type === 'route' || b.type === 'route') return false;
      if (a.type === 'tune-asset' || b.type === 'tune-asset') return false;
      if (a.type === 'product' || b.type === 'product') return false;
      return String(a.id) === String(b.id) && (a.type || 'article') === (b.type || 'article');
    };
    const updated = {
      ...rc,
      curated: (rc.curated || []).filter((i) => !same(i, entry)),
    };
    setPageData((prev) => ({ ...prev, metadata: { ...prev.metadata, related_content: updated } }));
  };

  // Load version history for the current page
  const loadVersionHistory = async () => {
    if (!pageData.pageId || isNewPage) return;

    setLoadingVersions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/content/pages/${pageData.pageId}/versions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setVersionHistory(data.versions || []);
      } else if (response.status !== 401) {
        console.error('Version history API error:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error loading version history:', error);
      showError('Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  // Preview a saved version snapshot (read-only)
  const viewVersion = (versionId) => {
    setPreviewVersionId(versionId);
    setShowVersionPreview(true);
  };

  const closeVersionPreview = () => {
    setShowVersionPreview(false);
    setPreviewVersionId(null);
  };

  // Restore a previous version
  const restoreVersion = async (versionId) => {
    if (!window.confirm('Are you sure you want to restore this version? Your current changes will be saved as a new version.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/content/pages/${pageData.pageId}/restore/${versionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(`Successfully restored version ${data.restoredVersion}`);
        // Reload the page to show restored content
        window.location.reload();
      } else {
        showError('Failed to restore version');
      }
    } catch (error) {
      console.error('Error restoring version:', error);
      showError('Failed to restore version');
    }
  };

  // Toggle version history panel
  const toggleVersionHistory = () => {
    if (!showVersionHistory) {
      loadVersionHistory();
    }
    setShowVersionHistory(!showVersionHistory);
  };

  const initializePage = async () => {
    const pageId = searchParams.get('pageId');
    const title = searchParams.get('title') || '';
    const description = searchParams.get('description') || '';
    const type = searchParams.get('type') || 'general';

    if (!pageId) {
      showError('Page ID is required');
      navigate('/admin/pages');
      return;
    }

    // Seed the editable page-ID input with the value from the URL
    setPageIdInput(pageId);

    setRenamePageIdInput('');
    setRenamePageIdError('');
    setRenamePageIdBusy(false);

    setPageData({
      pageId,
      title,
      description,
      type,
      html: '',
      metadata: {}
    });

    // Check if this is a new page or existing page
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/content/pages/${pageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle 401 Unauthorized - token expired
      if (response.status === 401) {
        console.log('401 Unauthorized - token expired, logging out');
        logout();
        return; // Exit early, logout will redirect
      }

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Non-JSON response when checking page existence:', text);
          await applyTuneTemplateIfNeeded(type, token);
          setIsNewPage(true);
        } else {
          const data = await response.json();
          console.log('DEBUG: PageEditor loaded data:', data);
          console.log('DEBUG: Metadata type:', typeof data.metadata);
          console.log('DEBUG: Metadata value:', data.metadata);

          const parsedMetadata = data.metadata ? (typeof data.metadata === 'string' ? JSON.parse(data.metadata) : data.metadata) : {};
          console.log('DEBUG: Parsed metadata:', parsedMetadata);

          const rawAssetIds = parsedMetadata.asset_ids;
          const hasExplicitAudioList =
            rawAssetIds &&
            typeof rawAssetIds === 'object' &&
            !Array.isArray(rawAssetIds) &&
            Array.isArray(rawAssetIds.audio);

          normalizeTunePageMetadataAssetIds(parsedMetadata);
          if (hasExplicitAudioList && parsedMetadata.asset_ids.audio.length === 0) {
            syncTuneAudioMetadataFields(parsedMetadata);
          }

          // Auto-detect asset IDs from existing page HTML if not already in metadata
          const pageHtml = data.html || '';
          const { notationIds, audioIds } = extractAssetIdsFromHtml(pageHtml);
          const existingNotations = parsedMetadata.asset_ids?.images || [];
          const existingAudios = parsedMetadata.asset_ids?.audio || [];
          if (notationIds.length > 0 && existingNotations.length === 0) {
            parsedMetadata.asset_ids = { ...(parsedMetadata.asset_ids || {}), images: notationIds };
          }
          // Only bootstrap audio from HTML when metadata never had an explicit asset_ids.audio list
          if (audioIds.length > 0 && existingAudios.length === 0 && !hasExplicitAudioList) {
            parsedMetadata.asset_ids = { ...(parsedMetadata.asset_ids || {}), audio: audioIds };
            syncTuneAudioMetadataFields(parsedMetadata);
          }

          setPageData(prev => ({
            ...prev,
            html: pageHtml,
            title: title || data.title || prev.title,
            description: description || data.description || prev.description,
            type: data.pageType || prev.type,
            featured: data.featured || false,
            home_announcement: data.home_announcement || false,
            is_hidden: data.is_hidden || false,
            metadata: parsedMetadata
          }));
          setIsNewPage(false);
          setIsLegacyPage(data.source === 'page_content');
        }
      } else {
        console.log(`Page ${pageId} not found, creating new page`);
        await applyTuneTemplateIfNeeded(type, token);
        setIsNewPage(true);
      }
    } catch (error) {
      console.error('Error checking page existence:', error);
      await applyTuneTemplateIfNeeded(type, token);
      setIsNewPage(true);
    } finally {
      setLoading(false);
    }
  };

  // For new tune-notation / abc-notation pages: fetch the matching layout template and pre-load HTML
  const applyTuneTemplateIfNeeded = async (type, token) => {
    const templateKey =
      type === 'abc-notation' ? 'abc-tune-page' : type === 'tune-notation' ? 'tune-page' : null;
    if (!templateKey) return;
    try {
      const res = await fetch(`/api/content/templates/${encodeURIComponent(templateKey)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const tpl = await res.json();
      if (!tpl || !tpl.template) return;

      // Apply with empty placeholder values — user fills them in via Insert Template or direct editing
      let html = tpl.template;
      const placeholders = tpl.placeholders || {};
      Object.keys(placeholders).forEach(key => {
        html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), '');
      });

      setPageData(prev => ({ ...prev, html }));
      setEditorKey(k => k + 1); // force editor to pick up the new HTML
      setTemplateBanner(
        templateKey === 'abc-tune-page'
          ? '🎼 ABC tune page template pre-loaded. Set ABC asset ID and text, or use Insert Template → abc-tune-page after seeding defaults.'
          : `🎵 Tune page template pre-loaded. Fill in your content using the toolbar's "📄 Insert Template" button, or edit the HTML directly.`
      );
    } catch (err) {
      console.warn(`Could not load ${templateKey} template:`, err);
    }
  };

  // Save metadata (title, description, type) to the backend
  const saveMetadata = async () => {
    try {
      // Validate page type format
      if (pageData.type && pageData.type.trim()) {
        const pageTypeRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
        if (!pageTypeRegex.test(pageData.type.trim())) {
          showError('Page type must be lowercase letters, numbers, and hyphens only (e.g., "my-custom-type")');
          return;
        }
      }

      const token = localStorage.getItem('token');
      if (!token) {
        showError('Authentication required. Please login first.');
        return;
      }

      // Always use the unified POST endpoint which handles both creation and updates accurately
      const url = `/api/content/pages/${pageData.pageId}`;
      const method = 'POST';

      const isTuneSave = pageData.type === 'tune-notation' || pageData.type === 'abc-notation';
      const metadataToSave = { ...pageData.metadata };
      if (isTuneSave) {
        syncTuneAudioMetadataFields(metadataToSave);
        metadataToSave.time_signature = normalizeTimeSignature(metadataToSave.time_signature);
      }
      const body = {
        title: pageData.title,
        description: pageData.description,
        type: pageData.type,
        featured: pageData.featured || false,
        home_announcement: pageData.home_announcement || false,
        is_hidden: pageData.is_hidden || false,
        metadata: JSON.stringify(metadataToSave)
      };
      // Tune pages: metadata-only (baked html preserved server-side).
      // Existing articles: never send html from Save Page Info — the HTML editor owns body content
      // (avoids overwriting a newer content save with stale parent state).
      // New pages: must send html to create the row.
      if (!isTuneSave && isNewPage) {
        body.html = pageData.html || '<p>New page content...</p>';
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        // Handle 401 Unauthorized - token expired
        if (response.status === 401) {
          console.log('401 Unauthorized - token expired, logging out');
          logout();
          return; // Exit early, logout will redirect
        }

        const errorText = await response.text();
        console.error('Error saving metadata:', errorText);
        showError('Failed to save page metadata');
        return;
      }

      const result = await response.json();
      console.log('Metadata saved successfully:', result);

      if (isTuneSave) {
        try {
          const syncRes = await fetch(`/api/content/pages/${pageData.pageId}/sync-assets`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (!syncRes.ok) {
            console.warn('sync-assets after save failed:', await syncRes.text());
          }
        } catch (syncErr) {
          console.warn('sync-assets after save error:', syncErr);
        }
        setPageData((prev) => {
          const nextMeta = { ...prev.metadata };
          syncTuneAudioMetadataFields(nextMeta);
          return { ...prev, metadata: nextMeta };
        });
      }

      showSuccess('Page saved successfully');
      setPreviewRefreshKey(k => k + 1);

      // If this was a new page, mark it as no longer new
      if (isNewPage) {
        setIsNewPage(false);
      } else {
        loadVersionHistory();
      }
    } catch (error) {
      console.error('Error saving metadata:', error);
      showError('Failed to save page metadata');
    }
  };

  const handleRenamePageIdInput = (raw) => {
    const slug = raw.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setRenamePageIdInput(slug);
    if (!slug) {
      setRenamePageIdError('');
    } else if (slug === pageData.pageId) {
      setRenamePageIdError('Must be different from the current ID');
    } else if (slug.length > 1 && !/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug)) {
      setRenamePageIdError('Must start and end with a letter or number');
    } else {
      setRenamePageIdError('');
    }
  };

  const handleRenamePageId = async () => {
    const slug = renamePageIdInput.trim();
    if (!slug || slug === pageData.pageId) {
      showError('Enter a new page ID that differs from the current one.');
      return;
    }
    if (renamePageIdError) {
      showError(renamePageIdError);
      return;
    }
    const msg =
      `Rename this page?\n\n` +
      `  From: ${pageData.pageId}\n` +
      `  To:   ${slug}\n\n` +
      `The public URL will change to /page/${slug}. Links on other pages or in emails are not updated automatically.`;
    if (!window.confirm(msg)) return;

    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication required.');
      return;
    }

    setRenamePageIdBusy(true);
    try {
      const response = await fetch(`/api/content/pages/${encodeURIComponent(pageData.pageId)}/rename`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newPageId: slug }),
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.error || 'Failed to rename page');
        return;
      }

      showSuccess(data.message || 'Page ID updated — reloading editor…');
      window.location.assign(`/admin/pages/edit?pageId=${encodeURIComponent(slug)}`);
    } catch (e) {
      console.error('Rename page:', e);
      showError('Failed to rename page');
    } finally {
      setRenamePageIdBusy(false);
    }
  };

  // Keep latest HTML in parent only after a successful content save (not on every keystroke).
  const handleContentChange = (newHtml) => {
    setPageData((prev) => (prev.html === newHtml ? prev : { ...prev, html: newHtml }));
  };

  // Validate and update the editable page ID (new pages only)
  const handlePageIdChange = (raw) => {
    // Auto-convert to valid slug while typing: spaces→hyphens, strip invalid chars
    const slug = raw.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setPageIdInput(slug);
    if (!slug) {
      setPageIdError('Page ID is required');
    } else if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug) && slug.length > 1) {
      setPageIdError('Must start and end with a letter or number');
    } else {
      setPageIdError('');
      setPageData(prev => ({ ...prev, pageId: slug }));
    }
  };

  const applyArticleLinkedProductIds = (ids) => {
    const idStr = ids.join(', ');
    const first = ids[0] ?? null;
    setSelectedProductIds(ids);
    setProductIdsInput(idStr);
    setSelectedProducts(products.filter((p) => ids.includes(p.id)));
    setPageData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        product_ids: ids.length ? ids.join(',') : '',
        product_id: first != null ? String(first) : '',
        productId: first,
        relatedProductId: first,
        productLink: first ? `/product/${first}` : null,
      },
    }));
  };

  const handleTuneProductSelectionChange = (ids) => {
    setSelectedProductIds(ids);
    setProductIdsInput(ids.join(', '));
    setSelectedProducts(products.filter((p) => ids.includes(p.id)));
    setPageData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, product_ids: ids.length ? ids.join(',') : '' },
    }));
    showSuccess(ids.length ? `Selected ${ids.length} related product(s)` : 'Cleared related products');
  };

  const handleArticleProductIdsChange = (value) => {
    setProductIdsInput(value);
    const ids = value
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id && !Number.isNaN(Number(id)))
      .map((id) => parseInt(id, 10));
    applyArticleLinkedProductIds(ids);
  };

  const removeArticleProduct = (productId) => {
    applyArticleLinkedProductIds(selectedProductIds.filter((id) => id !== productId));
  };

  const handleArticleProductSelectionChange = (ids) => {
    applyArticleLinkedProductIds(ids);
    showSuccess(ids.length ? `Linked ${ids.length} product(s)` : 'Cleared linked products');
  };

  const handleProductIdsChange = (value) => {
    setProductIdsInput(value);
    // Parse comma-separated IDs
    const ids = value.split(',')
      .map(id => id.trim())
      .filter(id => id && !isNaN(id))
      .map(id => parseInt(id));
    setSelectedProductIds(ids);

    // Update metadata
    setPageData(prev => ({
      ...prev,
      metadata: { ...prev.metadata, product_ids: value }
    }));

    // Update selected products display
    if (products.length > 0) {
      const selected = products.filter(p => ids.includes(p.id));
      setSelectedProducts(selected);
    }
  };

  const removeProduct = (productId) => {
    const newIds = selectedProductIds.filter(id => id !== productId);
    setSelectedProductIds(newIds);
    const newIdsString = newIds.join(', ');
    setProductIdsInput(newIdsString);
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));

    // Update metadata
    setPageData(prev => ({
      ...prev,
      metadata: { ...prev.metadata, product_ids: newIdsString }
    }));
  };

  // Article selection handlers

  const buildPageTagContext = () => ({
    title: pageData.title,
    description: pageData.description,
    type: pageData.type,
    pageId: pageData.pageId,
    html: pageData.html,
    metadata: pageData.metadata,
  });

  const handleAutoPopulateTags = () => {
    const existingTags = parseTagList(pageData.metadata.tags);
    const suggestedTags = suggestPageTags(buildPageTagContext(), { linkedProducts: selectedProducts });
    const mergedTags = mergeTagLists(existingTags, suggestedTags);
    const addedCount = mergedTags.length - existingTags.length;

    setPageData(prev => ({
      ...prev,
      metadata: { ...prev.metadata, tags: mergedTags.join(', ') },
    }));

    if (addedCount > 0) {
      showSuccess(`Added ${addedCount} tag suggestion(s) (${mergedTags.length} total)`);
    } else if (mergedTags.length > 0) {
      showSuccess(`No new tags — ${mergedTags.length} tag(s) already present`);
    } else {
      showError('Could not suggest tags. Add a title or content first.');
    }
  };

  // Auto-populate metadata from content
  const autoPopulateMetadata = () => {
    const text = pageData.html.replace(/<[^>]*>/g, ' ').toLowerCase();
    const allText = `${pageData.title} ${pageData.description} ${text}`.toLowerCase();

    const updates = {};

    const existingTags = parseTagList(pageData.metadata.tags);
    const suggestedTags = suggestPageTags(buildPageTagContext(), { linkedProducts: selectedProducts });
    const mergedTags = mergeTagLists(existingTags, suggestedTags);
    if (mergedTags.length > 0) {
      updates.tags = mergedTags.join(', ');
    }

    // Detect region from content
    if (!pageData.metadata.region || pageData.metadata.region.trim() === '') {
      if (allText.includes('cape breton') || allText.includes('cape-breton')) {
        updates.region = 'Cape Breton';
      } else if (allText.includes('scottish') || allText.includes('scotland')) {
        updates.region = 'Scottish';
      } else if (allText.includes('irish') || allText.includes('ireland')) {
        updates.region = 'Irish';
      } else if (allText.includes('canadian') || allText.includes('canada')) {
        updates.region = 'Canadian';
      } else if (allText.includes('american') || allText.includes('usa')) {
        updates.region = 'American';
      } else if (allText.includes('shetland')) {
        updates.region = 'Shetland';
      }
    }

    // Detect tune type from content (for tune-notation pages)
    if (pageData.type === 'tune-notation' && (!pageData.metadata.tune_type || pageData.metadata.tune_type.trim() === '')) {
      if (allText.includes('strathspey')) {
        updates.tune_type = 'Strathspey';
      } else if (allText.includes('reel')) {
        updates.tune_type = 'Reel';
      } else if (allText.includes('jig')) {
        updates.tune_type = 'Jig';
      } else if (allText.includes('waltz')) {
        updates.tune_type = 'Waltz';
      } else if (allText.includes('hornpipe')) {
        updates.tune_type = 'Hornpipe';
      } else if (allText.includes('march')) {
        updates.tune_type = 'March';
      } else if (allText.includes('air') || allText.includes('slow air')) {
        updates.tune_type = 'Air';
      }
    }

    // Detect time signature from content
    if (pageData.type === 'tune-notation' && (!pageData.metadata.time_signature || pageData.metadata.time_signature.trim() === '')) {
      if (allText.match(/\b9[/-]8\b/)) {
        updates.time_signature = '9/8';
      } else if (allText.match(/\b6[/-]8\b/)) {
        updates.time_signature = '6/8';
      } else if (allText.match(/\b4[/-]4\b/)) {
        updates.time_signature = '4/4';
      } else if (allText.match(/\b2[/-]4\b/)) {
        updates.time_signature = '2/4';
      } else if (allText.match(/\b3[/-]4\b/)) {
        updates.time_signature = '3/4';
      } else if (updates.tune_type === 'Slip Jig' || allText.includes('slip jig')) {
        updates.time_signature = '9/8';
      } else if (updates.tune_type === 'Jig') {
        updates.time_signature = '6/8';
      } else if (updates.tune_type === 'Reel') {
        updates.time_signature = '4/4';
      } else if (updates.tune_type === 'Waltz') {
        updates.time_signature = '3/4';
      }
    }

    // Detect author from content (for articles)
    if (pageData.type === 'article' && (!pageData.metadata.author || pageData.metadata.author.trim() === '')) {
      const authorPatterns = [
        /(?:Author|By|Written by):\s*([^<\n]+)/i,
        /<strong>Author:<\/strong>\s*([^<\n]+)/i,
        /<b>Author:<\/b>\s*([^<\n]+)/i
      ];

      for (const pattern of authorPatterns) {
        const match = pageData.html.match(pattern);
        if (match && match[1]) {
          updates.author = match[1].trim().replace(/<[^>]*>/g, '');
          break;
        }
      }

      // Default to Paul S. Cranford if not found
      if (!updates.author) {
        updates.author = 'Paul S. Cranford';
      }
    }

    // Detect category from tags (for articles)
    if (pageData.type === 'article' && (!pageData.metadata.category || pageData.metadata.category.trim() === '')) {
      if (allText.includes('biography') || allText.includes('life of') || allText.includes('born in')) {
        updates.category = 'Biography';
      } else if (allText.includes('history') || allText.includes('historical')) {
        updates.category = 'History';
      } else if (allText.includes('culture') || allText.includes('cultural') || allText.includes('tradition')) {
        updates.category = 'Cultural';
      } else if (allText.includes('interview') || allText.includes('conversation with')) {
        updates.category = 'Interviews';
      } else if (allText.includes('theory') || allText.includes('technique') || allText.includes('teaching')) {
        updates.category = 'Music Theory';
      }
    }

    // Apply updates
    setPageData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        ...updates
      }
    }));

    showSuccess(`Auto-populated ${Object.keys(updates).length} field(s) from content`);
  };

  const handleRefreshAssets = async () => {
    if (!window.confirm('This will re-download the GIF and MP3 from the source URL and update the image/audio links in the content. Manual edits to these links will be lost. Continue?')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/content/import/tunes/${pageData.pageId}/refresh-assets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        if (result.html) {
          setPageData(prev => ({ ...prev, html: result.html }));
          setEditorKey(prev => prev + 1); // Force editor to reload content
          showSuccess(result.message);
        } else {
          showSuccess(result.message);
        }
      } else {
        showError(result.error || 'Failed to refresh assets');
      }
    } catch (error) {
      console.error('Refresh assets error:', error);
      showError('Failed to refresh assets');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    window.open(`/page/${pageData.pageId}`, '_blank');
  };

  const handleBack = () => {
    navigate('/admin/pages');
  };

  const handleDelete = async () => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete page "${pageData.pageId}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showError('Authentication required. Please login first.');
        return;
      }

      const response = await fetch(`/api/content/pages/${pageData.pageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Handle 401 Unauthorized - token expired
        if (response.status === 401) {
          console.log('401 Unauthorized - token expired, logging out');
          logout();
          return; // Exit early, logout will redirect
        }

        const errorData = await response.json();
        showError(errorData.error || 'Failed to delete page');
        return;
      }

      showSuccess('Page deleted successfully');
      // Redirect to pages list after short delay
      setTimeout(() => {
        navigate('/admin/pages');
      }, 1500);
    } catch (error) {
      console.error('Error deleting page:', error);
      showError('Failed to delete page');
    }
  };

  if (loading) {
    return (
      <div className="page-editor-container container">
        <div className="page-editor-content content-card">
          <div className="loading">Loading page editor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-editor-container container">
      <div className="page-editor-content content-card">
        {/* Template auto-applied banner */}
        {templateBanner && (
          <div className="pe-template-banner">
            <span>{templateBanner}</span>
            <button className="pe-banner-close" onClick={() => setTemplateBanner('')}>×</button>
          </div>
        )}

        <div className="page-editor-header">
          <div className="page-editor-title">
            <h2>
              {isNewPage ? 'Create New Page' : 'Edit Page'}
            </h2>

            {isNewPage ? (
              <div className="pe-pageid-edit">
                <label htmlFor="pe-pageid-input">Page ID</label>
                <div className="pe-pageid-row">
                  <input
                    id="pe-pageid-input"
                    type="text"
                    className={`pe-pageid-input ${pageIdError ? 'has-error' : ''}`}
                    value={pageIdInput}
                    onChange={e => handlePageIdChange(e.target.value)}
                    placeholder="e.g. alex-fs-pipe-reel"
                    spellCheck={false}
                  />
                  <span className="pe-pageid-preview">
                    /page/<strong>{pageData.pageId || '…'}</strong>
                  </span>
                </div>
                {pageIdError && <span className="pe-pageid-error">{pageIdError}</span>}
                <small className="pe-pageid-hint">Lowercase letters, numbers and hyphens only. This becomes the page URL.</small>
              </div>
            ) : (
              <div className="pe-pageid-existing">
                <div className="page-id">
                  Page ID: <code>{pageData.pageId}</code>
                  <span className="pe-pageid-url-label"> · Public URL: <code>/page/{pageData.pageId}</code></span>
                </div>
                <details className="pe-pageid-rename-details">
                  <summary className="pe-pageid-rename-summary">Change page ID (URL slug)</summary>
                  <p className="form-text pe-pageid-rename-warning">
                    Updates the database and this page’s URL. Update bookmarks and any links elsewhere that still point to the old path.
                  </p>
                  <div className="pe-pageid-row pe-pageid-rename-row">
                    <input
                      type="text"
                      className={`pe-pageid-input ${renamePageIdError ? 'has-error' : ''}`}
                      value={renamePageIdInput}
                      onChange={(e) => handleRenamePageIdInput(e.target.value)}
                      placeholder={`New ID (e.g. ${pageData.pageId})`}
                      spellCheck={false}
                      disabled={renamePageIdBusy}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleRenamePageId}
                      disabled={
                        renamePageIdBusy ||
                        !renamePageIdInput.trim() ||
                        !!renamePageIdError ||
                        renamePageIdInput.trim() === pageData.pageId
                      }
                    >
                      {renamePageIdBusy ? 'Renaming…' : 'Rename'}
                    </button>
                  </div>
                  {renamePageIdError && <span className="pe-pageid-error">{renamePageIdError}</span>}
                  <small className="pe-pageid-hint">Same rules as new pages: lowercase, numbers, hyphens; start/end with a letter or digit.</small>
                </details>
              </div>
            )}
          </div>

          <div className="page-editor-actions">
            <button
              className="btn btn-secondary"
              onClick={handleBack}
            >
              {getIcon('arrow-left')} Back to Pages
            </button>
            <button
              className="btn btn-outline"
              onClick={handlePreview}
            >
              {getIcon('eye')} Preview
            </button>
          </div>
        </div>

        <div className="page-metadata">
          <div className="metadata-header">
            <h3>Page Information</h3>
            <div className="metadata-actions">
              <button
                className="btn btn-secondary btn-sm"
                onClick={autoPopulateMetadata}
                title="Auto-generate tags and metadata from content"
              >
                ✨ Auto-Fill Metadata
              </button>
              {(pageData.type === 'tune-notation' || pageData.type === 'abc-notation') &&
                pageData.metadata &&
                (pageData.metadata.source_url || pageData.metadata.source) && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleRefreshAssets}
                  title={`Refresh assets from source: ${pageData.metadata.source_url || pageData.metadata.source}`}
                  style={{ marginLeft: '8px' }}
                >
                  {getIcon('refresh')} Refresh Assets
                </button>
              )}
              {!isNewPage && (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleDelete}
                  title="Delete this page permanently"
                >
                  {getIcon('delete')} Delete Page
                </button>
              )}
              <button
                className="btn btn-primary btn-sm"
                onClick={saveMetadata}
              >
                {getIcon('save')} Save Page Info
              </button>
            </div>
          </div>

          <div className="metadata-fields metadata-grid">
            <div className="form-group form-group-full metadata-title-type-row">
              <div className="metadata-title-field">
                <label htmlFor="title">Page Title</label>
                <input
                  type="text"
                  id="title"
                  value={pageData.title}
                  onChange={(e) => setPageData(prev => ({ ...prev, title: e.target.value }))}
                  className="form-control"
                  placeholder="Enter page title"
                />
              </div>
              <div className="metadata-type-field">
                <label htmlFor="type">Page Type</label>
                <select
                  id="type"
                  value={pageData.type}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPageData(prev => ({
                      ...prev,
                      type: val
                    }));
                  }}
                  className="form-control"
                >
                  {allPageTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group form-group-full metadata-description-field">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={pageData.description}
                onChange={(e) => setPageData(prev => ({ ...prev, description: e.target.value }))}
                className="form-control"
                placeholder="Enter page description"
                rows="2"
              />
            </div>

            <div className="metadata-flags-row">
              <div className="metadata-flag-item">
                <label className="metadata-flag-heading" htmlFor="featured">Featured</label>
                <label htmlFor="featured" className="metadata-flag-control" title="Pin this page to the top of category lists like Articles and Tunes indices">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={pageData.featured || false}
                    onChange={(e) => setPageData(prev => ({ ...prev, featured: e.target.checked }))}
                  />
                  <span>⭐ Pin to Top</span>
                </label>
              </div>

              <div className="metadata-flag-item">
                <label className="metadata-flag-heading" htmlFor="is_hidden">Visibility</label>
                <label htmlFor="is_hidden" className="metadata-flag-control" title="Hide this page from the public site, browse lists, and search — admins can still edit it">
                  <input
                    type="checkbox"
                    id="is_hidden"
                    checked={pageData.is_hidden || false}
                    onChange={(e) => setPageData(prev => ({ ...prev, is_hidden: e.target.checked }))}
                  />
                  <span>🙈 Hide from site</span>
                </label>
                <p className="metadata-flag-hint">Direct links return “not found” for visitors; page stays in admin.</p>
              </div>

              {HOME_ANNOUNCEMENT_PAGE_TYPES.has(pageData.type) && (
                <div className="metadata-flag-item">
                  <label className="metadata-flag-heading" htmlFor="home_announcement">Home announcement</label>
                  <label htmlFor="home_announcement" className="metadata-flag-control" title="Show this article as a small announcement box on the home page">
                    <input
                      type="checkbox"
                      id="home_announcement"
                      checked={pageData.home_announcement || false}
                      onChange={(e) => setPageData(prev => ({ ...prev, home_announcement: e.target.checked }))}
                    />
                    <span>📢 Show on home page</span>
                  </label>
                  <p className="metadata-flag-hint">Orange box on home links to this article until you uncheck this.</p>
                </div>
              )}
            </div>
          </div>

          {(pageData.type === 'tune-notation' || pageData.type === 'abc-notation') && (
            <div className="metadata-fields metadata-grid" style={{ marginTop: '0.75rem' }}>
              <div className="form-group form-group-full">
                <label htmlFor="source_url">Original page URL · <code>source_url</code></label>
                <input
                  type="url"
                  id="source_url"
                  value={pageData.metadata.source_url || ''}
                  onChange={(e) => setPageData(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, source_url: e.target.value }
                  }))}
                  className="form-control"
                  placeholder="https://www.cranfordpub.com/tunes/..."
                />
                <small className="form-text text-muted">
                  Stored in metadata as <code>source_url</code> — same URL as <strong>📎 View Original Page</strong> and <strong>Refresh assets</strong>. If <code>source_url</code> is empty but that link is in the page HTML, loading the public tune page or this editor triggers a one-time save of <code>source_url</code> from the link (no manual copy needed).
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="tta_url">The Tune Archive URL · <code>tta_url</code></label>
                <input
                  type="url"
                  id="tta_url"
                  value={pageData.metadata.tta_url || ''}
                  onChange={(e) => setPageData(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, tta_url: e.target.value }
                  }))}
                  className="form-control"
                  placeholder="https://tunearch.org/wiki/..."
                />
                <small className="form-text text-muted">
                  Displayed on the tune page as an admin nav link. e.g. <code>https://tunearch.org/wiki/Earl_Grey</code>
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="cbfiddle_url">Cape Breton Fiddle URL · <code>cbfiddle_url</code></label>
                <input
                  type="url"
                  id="cbfiddle_url"
                  value={pageData.metadata.cbfiddle_url || ''}
                  onChange={(e) => setPageData(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, cbfiddle_url: e.target.value }
                  }))}
                  className="form-control"
                  placeholder="https://cbfiddle.com/rx/tune/..."
                />
                <small className="form-text text-muted">
                  Displayed on the tune page as an admin nav link. e.g. <code>https://cbfiddle.com/rx/tune/t1885.html</code>
                </small>
              </div>
            </div>
          )}

          {/* Type-specific metadata fields (notation + ABC tune pages) */}
          {(pageData.type === 'tune-notation' || pageData.type === 'abc-notation') && (
            <div className="type-specific-metadata">
              <h4 className="metadata-section-title">
                🎵 Tune Metadata
                <HelpPopup title="Tune Page Metadata Guide" position="right">
                  <p><strong>Tune metadata</strong> provides structured information about the musical piece.</p>

                  <p><strong>Key Fields:</strong></p>
                  <ul>
                    <li><strong>Original page URL:</strong> Legacy Cranford (or other) tune page—drives the nav link and asset refresh.</li>
                    <li><strong>Artist/Composer:</strong> Who wrote or recorded it (e.g., "Winston Fitzgerald")</li>
                    <li><strong>Tune Type, Key, Time:</strong> Choose from the dropdown lists, or use <strong>+ Add new entry…</strong> for a value not listed.</li>
                    <li><strong>Recording/Album:</strong> Where this version appears</li>
                  </ul>

                  <div className="tip">
                    💡 <strong>Complete Workflow:</strong>
                    <ol>
                      <li>Fill in metadata fields here</li>
                      <li>Add SEO tags for discoverability</li>
                      <li>Use the Simple Editor to insert a tune page template</li>
                      <li>Link assets (notation image & audio) in the template</li>
                      <li>Link related products using the product picker</li>
                    </ol>
                  </div>

                  <div className="example-box">
                    <strong>Example Tune:</strong><br />
                    Artist: "Winston Fitzgerald"<br />
                    Type: "Reel"<br />
                    Key: "D Major"<br />
                    Time: "4/4"<br />
                    Album: "Music from Cape Breton Island"
                  </div>

                  <p><strong>Why Metadata Matters:</strong></p>
                  <ul>
                    <li>Improves search results (SEO)</li>
                    <li>Helps users find related tunes</li>
                    <li>Provides context for musicians</li>
                    <li>Enables filtering and categorization</li>
                  </ul>
                </HelpPopup>
              </h4>
              <div className="metadata-grid">
                {/* Row 1: Musical basics */}
                <div className="form-group">
                  <label htmlFor="artist">Artist/Composer</label>
                  <input
                    type="text"
                    id="artist"
                    value={pageData.metadata.artist || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, artist: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Winston Fitzgerald"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tune_type">Tune Type</label>
                  <TuneFieldSelectWithAdd
                    id="tune_type"
                    className="form-control"
                    fieldLabel="tune type"
                    highlightEmpty={false}
                    value={pageData.metadata.tune_type || ''}
                    options={tuneFieldOptions.tune_type}
                    onSave={(v) =>
                      setPageData((prev) => ({
                        ...prev,
                        metadata: { ...prev.metadata, tune_type: v },
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="key">Key Signature</label>
                  <TuneFieldSelectWithAdd
                    id="key"
                    className="form-control"
                    fieldLabel="key signature"
                    highlightEmpty={false}
                    value={pageData.metadata.key_signature || pageData.metadata.key || ''}
                    options={tuneFieldOptions.key_signature}
                    onSave={(v) =>
                      setPageData((prev) => ({
                        ...prev,
                        metadata: { ...prev.metadata, key_signature: v, key: v },
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="time_signature">Time Signature</label>
                  <TuneFieldSelectWithAdd
                    id="time_signature"
                    className="form-control"
                    fieldLabel="time signature"
                    highlightEmpty={false}
                    allowAddNew={false}
                    value={normalizeTimeSignature(pageData.metadata.time_signature) || ''}
                    options={tuneFieldOptions.time_signature}
                    onSave={(v) =>
                      setPageData((prev) => ({
                        ...prev,
                        metadata: {
                          ...prev.metadata,
                          time_signature: normalizeTimeSignature(v),
                        },
                      }))
                    }
                  />
                </div>

                {/* Row 2: Origin & context */}
                <div className="form-group">
                  <label htmlFor="region">Region</label>
                  <input
                    type="text"
                    id="region"
                    list="regions"
                    value={pageData.metadata.region || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, region: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Scottish, Irish, Cape Breton"
                  />
                  <datalist id="regions">
                    <option value="Scottish" />
                    <option value="Irish" />
                    <option value="Cape Breton" />
                    <option value="Canadian" />
                    <option value="American" />
                  </datalist>
                </div>

                <div className="form-group">
                  <label htmlFor="source">Source / citation (text)</label>
                  <input
                    type="text"
                    id="source"
                    value={pageData.metadata.source || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, source: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="Short citation (not the web link—use Original page URL above)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="recording_album">Recording/Album</label>
                  <input
                    type="text"
                    id="recording_album"
                    value={pageData.metadata.recording_album || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, recording_album: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Music from Cape Breton Island"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="recording_year">Recording Year</label>
                  <input
                    type="number"
                    id="recording_year"
                    value={pageData.metadata.recording_year || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, recording_year: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., 1976"
                    min="1900"
                    max="2100"
                  />
                </div>

                {/* Row 3: Publication reference */}
                <div className="form-group">
                  <label htmlFor="collection_book">Book/Collection</label>
                  <input
                    type="text"
                    id="collection_book"
                    value={pageData.metadata.collection_book || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, collection_book: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., The Skye Collection, O'Neill's 1001"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="page_track_no">Page/Track No</label>
                  <input
                    type="text"
                    id="page_track_no"
                    value={pageData.metadata.page_track_no || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, page_track_no: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., 42, Track 5"
                  />
                </div>

                {/* Row 4: Assets (notation images + audio files) */}
                <div className="form-group form-group-full tune-assets-row">
                  <label className="tune-assets-label">🖼 Notation &amp; 🎵 Audio Assets</label>
                  <div className="tune-assets-grid">
                    <div className="tune-asset-field">
                      <span className="tune-asset-field-label">Notation images</span>
                      <MultiAssetList
                        isImage={true}
                        fileType="image"
                        ids={pageData.metadata?.asset_ids?.images?.map(Number).filter(Boolean) || []}
                        onChange={ids => setPageData(prev => ({
                          ...prev,
                          metadata: {
                            ...prev.metadata,
                            asset_ids: { ...(prev.metadata.asset_ids || {}), images: ids }
                          }
                        }))}
                        addPlaceholder="Search notation images…"
                      />
                    </div>
                    <div className="tune-asset-field">
                      <span className="tune-asset-field-label">
                        Audio (MP3)
                        {(pageData.metadata?.asset_ids?.audio?.length === 0 ||
                          (!pageData.metadata?.asset_ids?.audio && !pageData.metadata?.mp3_asset_id)) && (
                          <span className="tune-asset-no-audio"> — section hidden on page</span>
                        )}
                      </span>
                      <MultiAssetList
                        isImage={false}
                        fileType="music"
                        ids={(() => {
                          const arr = pageData.metadata?.asset_ids?.audio;
                          if (arr && arr.length > 0) return arr.map(Number).filter(Boolean);
                          const legacy = pageData.metadata?.mp3_asset_id;
                          return legacy ? [parseInt(legacy, 10)] : [];
                        })()}
                        onChange={(ids) => setPageData((prev) => {
                          const nextMeta = {
                            ...prev.metadata,
                            asset_ids: { ...(prev.metadata.asset_ids || {}), audio: ids },
                          };
                          syncTuneAudioMetadataFields(nextMeta);
                          return { ...prev, metadata: nextMeta };
                        })}
                        addPlaceholder="Search MP3 / audio files…"
                      />
                    </div>
                  </div>
                  <small className="form-text">
                    Multiple files are supported. If <strong>Audio is empty</strong>, the audio player section is hidden on the published tune page.
                    Auto-detected from the page HTML on load.
                  </small>
                </div>

                {/* Row 5: Related products (full-width) */}
                <div className="form-group form-group-full">
                  <label htmlFor="product_ids">📦 Related Products</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      id="product_ids"
                      value={productIdsInput}
                      onChange={(e) => handleProductIdsChange(e.target.value)}
                      className="form-control"
                      placeholder="e.g., 624, 627, 639"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowProductPicker(true)}
                      className="btn btn-secondary"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      🔍 Browse Products
                    </button>
                  </div>
                  <small className="form-text">Products this tune appears in (shown in "Featured In"). Type IDs or Browse.</small>
                  {selectedProducts.length > 0 && (
                    <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'var(--color-surface)', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--color-text-secondary)' }}>
                        Selected Products:
                      </div>
                      {selectedProducts.map(product => (
                        <div key={product.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px',
                          marginBottom: '4px',
                          backgroundColor: 'var(--color-background)',
                          borderRadius: '4px'
                        }}>
                          <span style={{ fontSize: '13px' }}>
                            <strong>{product.name}</strong> {product.sku ? `(${product.sku})` : ''} — ID: {product.id}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeProduct(product.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--color-text-secondary)',
                              cursor: 'pointer',
                              fontSize: '18px',
                              padding: '0 8px'
                            }}
                            title="Remove product"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="tags">
                    Tags (comma-separated)
                    <HelpPopup title="Understanding Tags & SEO" position="right">
                      <p><strong>Tags are crucial for page discovery and SEO!</strong></p>

                      <div className="tip">
                        💡 <strong>What are Tags?</strong><br />
                        Tags are keywords that describe your page content. They help:
                        <ul>
                          <li>Users find related content</li>
                          <li>Search engines understand your page</li>
                          <li>Organize and categorize your pages</li>
                        </ul>
                      </div>

                      <p><strong>How Tags Become Meta Tags:</strong></p>
                      <p>When you save a page, your tags are automatically converted to HTML meta tags:</p>
                      <div className="example-box">
                        <code>&lt;meta name="keywords" content="your,tags,here"&gt;</code>
                      </div>

                      <p><strong>Best Practices:</strong></p>
                      <ul>
                        <li>Use 5-10 relevant keywords</li>
                        <li>Separate with commas: <code>fiddle, cape breton, traditional</code></li>
                        <li>Be specific: "cape-breton-fiddle" better than "music"</li>
                        <li>Include variations: "strathspey, strathspeys"</li>
                      </ul>

                      <div className="warning">
                        ⚠️ <strong>Important:</strong> Tags also help connect related tunes and pages. Use consistent tags across related content!
                      </div>

                      <p><strong>Example Tag Sets:</strong></p>
                      <ul>
                        <li><strong>Tune:</strong> "reel, scottish, JSScott, wedding-reel"</li>
                        <li><strong>Biography:</strong> "winston-fitzgerald, fiddler, cape-breton, biography"</li>
                        <li><strong>Book Index:</strong> "book-index, cape-breton-companion, fiddle-tunes"</li>
                      </ul>
                    </HelpPopup>
                  </label>
                  <div className="input-with-button tags-input-row">
                    <input
                      type="text"
                      id="tags"
                      value={pageData.metadata.tags || ''}
                      onChange={(e) => setPageData(prev => ({
                        ...prev,
                        metadata: { ...prev.metadata, tags: e.target.value }
                      }))}
                      className="form-control"
                      placeholder="e.g., strathspey, scottish, skinner, classic"
                    />
                    <button
                      type="button"
                      className="generate-button tags-generate-button"
                      onClick={handleAutoPopulateTags}
                      title="Suggest tags from page title, content, metadata, and linked products"
                    >
                      ✨ Auto-Populate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Article metadata fields */}
          {pageData.type === 'article' && (
            <div className="type-specific-metadata">
              <h4 className="metadata-section-title">📄 Article Metadata</h4>
              <div className="metadata-grid">
                <div className="form-group">
                  <label htmlFor="author">Author</label>
                  <input
                    type="text"
                    id="author"
                    value={pageData.metadata.author || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, author: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Paul Stewart Cranford"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="publicationDate">Publication Date</label>
                  <input
                    type="date"
                    id="publicationDate"
                    value={pageData.metadata.publicationDate || pageData.metadata.published_date || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, publicationDate: e.target.value }
                    }))}
                    className="form-control"
                  />
                  <small className="form-text">Shown as year, e.g. "Published: 1999"</small>
                </div>

                <div className="form-group">
                  <label htmlFor="category">Category</label>
                  <input
                    type="text"
                    id="category"
                    list="articleCategories"
                    value={pageData.metadata.category || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, category: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., History, Biography, Cultural"
                  />
                  <datalist id="articleCategories">
                    <option value="History" />
                    <option value="Biography" />
                    <option value="Cultural" />
                    <option value="Music Theory" />
                    <option value="Interviews" />
                  </datalist>
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="article_layout">Article style (theme)</label>
                  <select
                    id="article_layout"
                    className="form-control"
                    value={pageData.metadata.article_layout || 'default'}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, article_layout: e.target.value }
                    }))}
                  >
                    {articleLayoutChoices.map((row) => (
                      <option key={row.layout_key} value={row.layout_key}>
                        {row.name}{row.description ? ` — ${row.description}` : ''}
                      </option>
                    ))}
                  </select>
                  <small className="form-text">
                    Saved in page metadata as <code>article_layout</code>. Adds a viewer wrapper class on the public page; CSS/HTML for imports is edited under Admin → Templates → Article layouts.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="source">Source/Publication</label>
                  <input
                    type="text"
                    id="source"
                    value={pageData.metadata.source || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, source: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Silver Apple News"
                  />
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="article_product_ids">📦 Linked Products</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      id="article_product_ids"
                      value={productIdsInput}
                      onChange={(e) => handleArticleProductIdsChange(e.target.value)}
                      className="form-control"
                      placeholder="e.g., 640, 624, 627"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowArticleProductPicker(true)}
                      className="btn btn-secondary"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      🔍 Browse Products
                    </button>
                  </div>
                  <small className="form-text">
                    Link one or more products/books to this article. Type IDs or use Browse to select multiple.
                  </small>
                  {selectedProducts.length > 0 && (
                    <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'var(--color-surface)', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--color-text-secondary)' }}>
                        Linked Products:
                      </div>
                      {selectedProducts.map((product) => (
                        <div key={product.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px',
                          marginBottom: '4px',
                          backgroundColor: 'var(--color-background)',
                          borderRadius: '4px'
                        }}>
                          <span style={{ fontSize: '13px' }}>
                            <strong>{product.name}</strong>
                            {product.sku ? ` (${product.sku})` : ''} — ID: {product.id}
                            {' '}
                            <a href={`/product/${product.id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#238636', fontSize: '0.8rem' }}>View →</a>
                          </span>
                          <button
                            type="button"
                            onClick={() => removeArticleProduct(product.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--color-text-secondary)',
                              cursor: 'pointer',
                              fontSize: '18px',
                              padding: '0 8px'
                            }}
                            title="Remove linked product"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="tags">
                    Tags (comma-separated)
                    <HelpPopup title="Understanding Tags & SEO" position="right">
                      <p><strong>Tags are crucial for page discovery and SEO!</strong></p>

                      <div className="tip">
                        💡 <strong>What are Tags?</strong><br />
                        Tags are keywords that describe your page content. They help:
                        <ul>
                          <li>Users find related content</li>
                          <li>Search engines understand your page</li>
                          <li>Organize and categorize your pages</li>
                        </ul>
                      </div>

                      <p><strong>How Tags Become Meta Tags:</strong></p>
                      <p>When you save a page, your tags are automatically converted to HTML meta tags:</p>
                      <div className="example-box">
                        <code>&lt;meta name="keywords" content="your,tags,here"&gt;</code>
                      </div>

                      <p><strong>Best Practices:</strong></p>
                      <ul>
                        <li>Use 5-10 relevant keywords</li>
                        <li>Separate with commas: <code>fiddle, cape breton, traditional</code></li>
                        <li>Be specific: "cape-breton-fiddle" better than "music"</li>
                        <li>Include variations: "strathspey, strathspeys"</li>
                      </ul>

                      <div className="warning">
                        ⚠️ <strong>Important:</strong> Tags also help connect related tunes and pages. Use consistent tags across related content!
                      </div>

                      <p><strong>Example Tag Sets:</strong></p>
                      <ul>
                        <li><strong>Tune:</strong> "reel, scottish, JSScott, wedding-reel"</li>
                        <li><strong>Biography:</strong> "winston-fitzgerald, fiddler, cape-breton, biography"</li>
                        <li><strong>Book Index:</strong> "book-index, cape-breton-companion, fiddle-tunes"</li>
                      </ul>
                    </HelpPopup>
                  </label>
                  <div className="input-with-button tags-input-row">
                    <input
                      type="text"
                      id="tags"
                      value={pageData.metadata.tags || ''}
                      onChange={(e) => setPageData(prev => ({
                        ...prev,
                        metadata: { ...prev.metadata, tags: e.target.value }
                      }))}
                      className="form-control"
                      placeholder="e.g., cape breton, history, fiddlers"
                    />
                    <button
                      type="button"
                      className="generate-button tags-generate-button"
                      onClick={handleAutoPopulateTags}
                      title="Suggest tags from page title, content, metadata, and linked products"
                    >
                      ✨ Auto-Populate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Related Content Sections — all page types ── */}
          {!isNewPage && (
            <div className="type-specific-metadata">
              <h4 className="metadata-section-title">🔗 Related Content Sections
                <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: '4px' }}>Changes apply on save — no template re-apply needed</span>
              </h4>

              {/* ── Sections mode table ─────────────────────────────────── */}
              {(() => {
                const DEFAULT_SECTION_LIMITS = { products: 24, articles: 8, tunes: 12, curated: 24 };
                const DEFAULT_SECTION_HEADINGS = {
                  products: '📚 Featured In',
                  articles: '📰 Related Articles',
                  tunes: '🎵 Related Tunes',
                  curated: '🔗 Also See',
                };
                const rc = pageData.metadata?.related_content || {};
                const sections = rc.sections || {};
                const limits = rc.section_limits || {};
                const sectionTitles = rc.section_titles || {};
                const limitValue = (key) => {
                  const v = parseInt(limits[key], 10);
                  if (Number.isFinite(v) && v >= 1) return v;
                  return DEFAULT_SECTION_LIMITS[key] ?? 8;
                };
                const setSection = (key, val) => setPageData(prev => ({
                  ...prev,
                  metadata: { ...prev.metadata, related_content: {
                    ...(prev.metadata?.related_content || {}),
                    sections: { ...(prev.metadata?.related_content?.sections || {}), [key]: val }
                  }}
                }));
                const setSectionLimit = (key, rawVal) => {
                  const v = parseInt(String(rawVal), 10);
                  const num = !Number.isFinite(v)
                    ? DEFAULT_SECTION_LIMITS[key] ?? 8
                    : Math.max(1, Math.min(100, v));
                  setPageData(prev => ({
                    ...prev,
                    metadata: {
                      ...prev.metadata,
                      related_content: {
                        ...(prev.metadata?.related_content || {}),
                        section_limits: {
                          ...(prev.metadata?.related_content?.section_limits || {}),
                          [key]: num
                        }
                      }
                    }
                  }));
                };

                const setSectionTitle = (key, safeVal) => {
                  const trimmed = String(safeVal ?? '').trim();
                  setPageData(prev => {
                    const prevRc = prev.metadata?.related_content || {};
                    const nextTitles = { ...(prevRc.section_titles || {}) };
                    if (!trimmed) delete nextTitles[key];
                    else nextTitles[key] = String(safeVal).slice(0, 200);
                    const nextRc = { ...prevRc };
                    if (Object.keys(nextTitles).length === 0) delete nextRc.section_titles;
                    else nextRc.section_titles = nextTitles;
                    return {
                      ...prev,
                      metadata: { ...prev.metadata, related_content: nextRc }
                    };
                  });
                };

                const sectionDefs = [
                  { key: 'products', icon: '📚', label: 'Featured In',
                    options: [{ value:'explicit',label:'Explicit (picks below)'},{value:'off',label:'Hidden'}],
                    default: 'explicit' },
                  { key: 'articles', icon: '📰', label: 'Related Articles',
                    options: [{value:'off',label:'Hidden'},{value:'tags',label:'By Tags (dynamic)'},{value:'explicit',label:'Explicit only'},{value:'both',label:'Explicit + Tags'}],
                    default: 'off' },
                  { key: 'tunes', icon: '🎵', label: 'Related Tunes',
                    options: [{value:'tags',label:'By Tags (dynamic)'},{value:'explicit',label:'Explicit only'},{value:'both',label:'Explicit + Tags'},{value:'off',label:'Hidden'}],
                    default: 'tags' },
                  { key: 'curated', icon: '🔗', label: 'Curated Picks',
                    options: [{value:'off',label:'Hidden'},{value:'on',label:'Show (mixed explicit)'}],
                    default: 'off' },
                ];

                return (
                  <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <p className="rc-sections-intro">
                      <strong>Max shown</strong> caps how many items appear per section on the live page (1–100)—including <strong>explicit picks</strong>. If you add more picks than this number, raise Max shown or they will be cut off. Adding a pick auto-raises the cap when needed. <strong>Section heading</strong> overrides the default title on tune pages and in the preview (<code>related_content.section_titles</code>).
                    </p>
                    <div className="rc-sections-panel">
                      {sectionDefs.map((sec, i) => {
                        const current = sections[sec.key] ?? sec.default;
                        const needsPicker = (sec.key === 'products' && current !== 'off')
                          || (sec.key === 'articles' && (current === 'explicit' || current === 'both'))
                          || (sec.key === 'tunes'    && (current === 'explicit' || current === 'both'))
                          || (sec.key === 'curated'  && current === 'on');
                        const sectionActive = current !== 'off' && !(sec.key === 'curated' && current !== 'on');
                        const stripeEven = i % 2 === 0;
                        return (
                          <div key={sec.key} className="rc-section-block">
                            {/* Section row */}
                            <div className={`rc-section-row ${stripeEven ? 'rc-section-row--even' : 'rc-section-row--odd'}`}>
                              <div className="rc-section-row-label">{sec.icon} {sec.label}</div>
                              <select
                                value={current}
                                onChange={e => setSection(sec.key, e.target.value)}
                                className="form-control"
                                style={{ fontSize: '0.82rem', padding: '4px 8px' }}
                              >
                                {sec.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                              <input
                                type="number"
                                min={1}
                                max={100}
                                title="Maximum items to show in this section"
                                disabled={!sectionActive}
                                value={limitValue(sec.key)}
                                onChange={(e) => setSectionLimit(sec.key, e.target.value)}
                                className="form-control"
                                style={{
                                  fontSize: '0.82rem',
                                  padding: '4px 6px',
                                  opacity: sectionActive ? 1 : 0.45
                                }}
                              />
                            </div>

                            {sectionActive && (
                              <div className={`rc-section-subpanel ${stripeEven ? 'rc-section-subpanel--even' : 'rc-section-subpanel--odd'}`}>
                                <label className="rc-section-label-muted">
                                  Heading on live page
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={DEFAULT_SECTION_HEADINGS[sec.key]}
                                  value={sectionTitles[sec.key] ?? ''}
                                  maxLength={200}
                                  onChange={(e) => {
                                    const v = e.target.value.replace(/[<>]/g, '').slice(0, 200);
                                    setSectionTitle(sec.key, v);
                                  }}
                                  style={{ fontSize: '0.82rem', padding: '5px 10px' }}
                                />
                              </div>
                            )}

                            {/* Explicit picks + search — always visible when this section uses the picker */}
                            {needsPicker && (() => {
                              const listKey = sec.key;
                              const rcNow = pageData.metadata?.related_content || {};
                              const items = rcNow[listKey] || [];
                              const isCurated = listKey === 'curated';
                              return (
                                <div className={`rc-picks-panel ${stripeEven ? 'rc-picks-panel--even' : 'rc-picks-panel--odd'} ${isCurated ? 'rc-picks-panel--curated' : 'rc-picks-panel--default'}`}>
                                  <div className="rc-picks-summary">
                                    Explicit picks ({items.length})
                                  </div>
                                  {items.length > 0 ? (
                                    <div className="rc-picks-chips">
                                      {items.map(item => {
                                        let itemId = item.id ?? item.href;
                                        let label = String(itemId ?? '');
                                        let icon = sec.icon;
                                        if (listKey === 'products') {
                                          const prod = products.find(p => String(p.id) === String(itemId));
                                          label = prod?.name || `Product #${itemId}`;
                                        } else if (listKey === 'curated') {
                                          if (item.type === 'route') {
                                            icon = '🔗';
                                            label = item.title || item.href || '';
                                            itemId = item.href;
                                          } else if (item.type === 'tune-asset') {
                                            icon = '🎼';
                                            label = item.title || parseAbcAssetTitle(abcTuneAssets.find((a) => String(a.id) === String(item.id))) || `ABC #${item.id}`;
                                            itemId = item.id;
                                          } else {
                                            icon = item.type === 'tune' ? '🎵' : item.type === 'product' ? '📚' : '📰';
                                            const prod = item.type === 'product' && products.find(p => String(p.id) === String(itemId));
                                            const page = item.type !== 'product' && allPages.find(p => p.page_id === itemId);
                                            label = prod?.name || page?.title || String(itemId);
                                          }
                                        } else {
                                          const page = allPages.find(p => p.page_id === itemId);
                                          label = page?.title || String(itemId);
                                        }
                                        return (
                                          <div key={`${listKey}-summary-${itemId}`} className="rc-pick-chip">
                                            <span>{icon}</span>
                                            <span className="rc-pick-chip-label" title={label}>{label}</span>
                                            <button type="button" onClick={() => (listKey === 'curated' ? removeCuratedPick(item) : removeTypedItem(listKey, itemId))}
                                              className="rc-pick-remove" aria-label={`Remove ${label}`}>×</button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <p className="rc-picks-empty">
                                      No picks yet — type below (at least 2 characters) to search and add.
                                    </p>
                                  )}
                                  <label className="rc-section-label-muted rc-section-label-muted--spaced">
                                    Search {listKey === 'products' ? 'products' : listKey === 'tunes' ? 'tunes' : listKey === 'articles' ? 'articles' : 'any page type, shop link, or product'}
                                  </label>
                                  <div className="rc-picker-wrap">
                                    <input
                                      type="text"
                                      value={pickerQuery[listKey]}
                                      onChange={e => handleTypedSearch(listKey, e.target.value)}
                                      className="form-control"
                                      placeholder={listKey === 'products' ? 'Search products…' : listKey === 'tunes' ? 'Search tunes…' : listKey === 'articles' ? 'Search articles…' : 'Search or paste /tune-asset/1850 (or id 1850), title, shop…'}
                                      style={{ fontSize: '0.82rem', padding: '8px 10px' }}
                                      autoComplete="off"
                                    />
                                    {pickerResults[listKey].length > 0 && (
                                      <div className={`rc-picker-dropdown ${isCurated ? 'rc-picker-dropdown--curated' : 'rc-picker-dropdown--default'}`}>
                                        {pickerResults[listKey].map(res => (
                                          <div key={res._key}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter') addTypedItem(listKey, res); }}
                                            onClick={() => addTypedItem(listKey, res)}
                                            className="rc-picker-result">
                                            <span className="rc-picker-result-label">{res.label}</span>
                                            <span className="rc-picker-result-sub">{res.sub}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Biography metadata fields */}
          {pageData.type === 'biography' && (
            <div className="type-specific-metadata">
              <h4 className="metadata-section-title">👤 Biography Metadata</h4>
              <div className="metadata-grid">
                <div className="form-group">
                  <label htmlFor="subject_name">Subject Name</label>
                  <input
                    type="text"
                    id="subject_name"
                    value={pageData.metadata.subject_name || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, subject_name: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Winston Fitzgerald"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="birth_date">Birth Date</label>
                  <input
                    type="date"
                    id="birth_date"
                    value={pageData.metadata.birth_date || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, birth_date: e.target.value }
                    }))}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="death_date">Death Date (if applicable)</label>
                  <input
                    type="date"
                    id="death_date"
                    value={pageData.metadata.death_date || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, death_date: e.target.value }
                    }))}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="nationality">Nationality/Region</label>
                  <input
                    type="text"
                    id="nationality"
                    value={pageData.metadata.nationality || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, nationality: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Cape Breton, Scottish, Irish"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="instruments">Instruments</label>
                  <input
                    type="text"
                    id="instruments"
                    value={pageData.metadata.instruments || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, instruments: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Fiddle, Piano, Guitar"
                  />
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="tags">
                    Tags (comma-separated)
                    <HelpPopup title="Understanding Tags & SEO" position="right">
                      <p><strong>Tags are crucial for page discovery and SEO!</strong></p>

                      <div className="tip">
                        💡 <strong>What are Tags?</strong><br />
                        Tags are keywords that describe your page content. They help:
                        <ul>
                          <li>Users find related content</li>
                          <li>Search engines understand your page</li>
                          <li>Organize and categorize your pages</li>
                        </ul>
                      </div>

                      <p><strong>How Tags Become Meta Tags:</strong></p>
                      <p>When you save a page, your tags are automatically converted to HTML meta tags:</p>
                      <div className="example-box">
                        <code>&lt;meta name="keywords" content="your,tags,here"&gt;</code>
                      </div>

                      <p><strong>Best Practices:</strong></p>
                      <ul>
                        <li>Use 5-10 relevant keywords</li>
                        <li>Separate with commas: <code>fiddle, cape breton, traditional</code></li>
                        <li>Be specific: "cape-breton-fiddle" better than "music"</li>
                        <li>Include variations: "strathspey, strathspeys"</li>
                      </ul>

                      <div className="warning">
                        ⚠️ <strong>Important:</strong> Tags also help connect related tunes and pages. Use consistent tags across related content!
                      </div>

                      <p><strong>Example Tag Sets:</strong></p>
                      <ul>
                        <li><strong>Tune:</strong> "reel, scottish, JSScott, wedding-reel"</li>
                        <li><strong>Biography:</strong> "winston-fitzgerald, fiddler, cape-breton, biography"</li>
                        <li><strong>Book Index:</strong> "book-index, cape-breton-companion, fiddle-tunes"</li>
                      </ul>
                    </HelpPopup>
                  </label>
                  <div className="input-with-button tags-input-row">
                    <input
                      type="text"
                      id="tags"
                      value={pageData.metadata.tags || ''}
                      onChange={(e) => setPageData(prev => ({
                        ...prev,
                        metadata: { ...prev.metadata, tags: e.target.value }
                      }))}
                      className="form-control"
                      placeholder="e.g., fiddler, composer, cape breton"
                    />
                    <button
                      type="button"
                      className="generate-button tags-generate-button"
                      onClick={handleAutoPopulateTags}
                      title="Suggest tags from page title, content, metadata, and linked products"
                    >
                      ✨ Auto-Populate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recording metadata fields */}
          {pageData.type === 'recording' && (
            <div className="type-specific-metadata">
              <h4 className="metadata-section-title">💿 Recording Metadata</h4>
              <div className="metadata-grid">
                <div className="form-group">
                  <label htmlFor="artist">Artist/Performer</label>
                  <input
                    type="text"
                    id="artist"
                    value={pageData.metadata.artist || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, artist: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Natalie MacMaster"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="album_title">Album Title</label>
                  <input
                    type="text"
                    id="album_title"
                    value={pageData.metadata.album_title || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, album_title: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., My Roots Are Showing"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="release_year">Release Year</label>
                  <input
                    type="number"
                    id="release_year"
                    value={pageData.metadata.release_year || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, release_year: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., 1998"
                    min="1900"
                    max="2100"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="label">Record Label</label>
                  <input
                    type="text"
                    id="label"
                    value={pageData.metadata.label || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, label: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Rounder Records"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="catalog_number">Catalog Number</label>
                  <input
                    type="text"
                    id="catalog_number"
                    value={pageData.metadata.catalog_number || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, catalog_number: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., CD-7025"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="format">Format</label>
                  <input
                    type="text"
                    id="format"
                    list="recordingFormats"
                    value={pageData.metadata.format || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, format: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., CD, LP, Digital"
                  />
                  <datalist id="recordingFormats">
                    <option value="CD" />
                    <option value="LP/Vinyl" />
                    <option value="Digital" />
                    <option value="Cassette" />
                    <option value="78 RPM" />
                  </datalist>
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="tags">
                    Tags (comma-separated)
                    <HelpPopup title="Understanding Tags & SEO" position="right">
                      <p><strong>Tags are crucial for page discovery and SEO!</strong></p>

                      <div className="tip">
                        💡 <strong>What are Tags?</strong><br />
                        Tags are keywords that describe your page content. They help:
                        <ul>
                          <li>Users find related content</li>
                          <li>Search engines understand your page</li>
                          <li>Organize and categorize your pages</li>
                        </ul>
                      </div>

                      <p><strong>How Tags Become Meta Tags:</strong></p>
                      <p>When you save a page, your tags are automatically converted to HTML meta tags:</p>
                      <div className="example-box">
                        <code>&lt;meta name="keywords" content="your,tags,here"&gt;</code>
                      </div>

                      <p><strong>Best Practices:</strong></p>
                      <ul>
                        <li>Use 5-10 relevant keywords</li>
                        <li>Separate with commas: <code>fiddle, cape breton, traditional</code></li>
                        <li>Be specific: "cape-breton-fiddle" better than "music"</li>
                        <li>Include variations: "strathspey, strathspeys"</li>
                      </ul>

                      <div className="warning">
                        ⚠️ <strong>Important:</strong> Tags also help connect related tunes and pages. Use consistent tags across related content!
                      </div>

                      <p><strong>Example Tag Sets:</strong></p>
                      <ul>
                        <li><strong>Tune:</strong> "reel, scottish, JSScott, wedding-reel"</li>
                        <li><strong>Biography:</strong> "winston-fitzgerald, fiddler, cape-breton, biography"</li>
                        <li><strong>Book Index:</strong> "book-index, cape-breton-companion, fiddle-tunes"</li>
                      </ul>
                    </HelpPopup>
                  </label>
                  <div className="input-with-button tags-input-row">
                    <input
                      type="text"
                      id="tags"
                      value={pageData.metadata.tags || ''}
                      onChange={(e) => setPageData(prev => ({
                        ...prev,
                        metadata: { ...prev.metadata, tags: e.target.value }
                      }))}
                      className="form-control"
                      placeholder="e.g., fiddle, cape breton, traditional"
                    />
                    <button
                      type="button"
                      className="generate-button tags-generate-button"
                      onClick={handleAutoPopulateTags}
                      title="Suggest tags from page title, content, metadata, and linked products"
                    >
                      ✨ Auto-Populate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Book Index metadata fields */}
          {pageData.type === 'book-index' && (
            <div className="type-specific-metadata">
              <h4 className="metadata-section-title">📚 Book Index Metadata</h4>
              <div className="metadata-grid">
                <div className="form-group">
                  <label htmlFor="book_name">Book Name</label>
                  <input
                    type="text"
                    id="book_name"
                    value={pageData.metadata.book_name || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, book_name: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., The Cape Breton Highland Collection"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="author">Author</label>
                  <input
                    type="text"
                    id="author"
                    value={pageData.metadata.author || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, author: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., Paul Cranford"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="volume_number">Volume Number</label>
                  <input
                    type="text"
                    id="volume_number"
                    value={pageData.metadata.volume_number || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, volume_number: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., 1, 2 (leave empty if not applicable)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sku">SKU</label>
                  <input
                    type="text"
                    id="sku"
                    value={pageData.metadata.sku || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, sku: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., CBHC-bk"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="related_product">Related Product</label>
                  <input
                    type="text"
                    id="related_product"
                    value={pageData.metadata.related_product || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, related_product: e.target.value }
                    }))}
                    className="form-control"
                    placeholder="e.g., cbhc-bk (product permalink)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="product_id">Product ID</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="number"
                      id="product_id"
                      value={pageData.metadata.product_id || ''}
                      onChange={(e) => setPageData(prev => ({
                        ...prev,
                        metadata: { ...prev.metadata, product_id: e.target.value }
                      }))}
                      className="form-control"
                      placeholder="e.g., 624"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowProductPicker(true)}
                      className="btn btn-secondary"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      🔍 Browse
                    </button>
                  </div>
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="tags">
                    Tags (comma-separated)
                    <HelpPopup title="Understanding Tags & SEO" position="right">
                      <p><strong>Tags are crucial for page discovery and SEO!</strong></p>

                      <div className="tip">
                        💡 <strong>What are Tags?</strong><br />
                        Tags are keywords that describe your page content. They help:
                        <ul>
                          <li>Users find related content</li>
                          <li>Search engines understand your page</li>
                          <li>Organize and categorize your pages</li>
                        </ul>
                      </div>

                      <p><strong>How Tags Become Meta Tags:</strong></p>
                      <p>When you save a page, your tags are automatically converted to HTML meta tags:</p>
                      <div className="example-box">
                        <code>&lt;meta name="keywords" content="your,tags,here"&gt;</code>
                      </div>

                      <p><strong>Best Practices:</strong></p>
                      <ul>
                        <li>Use 5-10 relevant keywords</li>
                        <li>Separate with commas: <code>fiddle, cape breton, traditional</code></li>
                        <li>Be specific: "cape-breton-fiddle" better than "music"</li>
                        <li>Include variations: "strathspey, strathspeys"</li>
                      </ul>

                      <div className="warning">
                        ⚠️ <strong>Important:</strong> Tags also help connect related tunes and pages. Use consistent tags across related content!
                      </div>

                      <p><strong>Example Tag Sets:</strong></p>
                      <ul>
                        <li><strong>Tune:</strong> "reel, scottish, JSScott, wedding-reel"</li>
                        <li><strong>Biography:</strong> "winston-fitzgerald, fiddler, cape-breton, biography"</li>
                        <li><strong>Book Index:</strong> "book-index, cape-breton-companion, fiddle-tunes"</li>
                      </ul>
                    </HelpPopup>
                  </label>
                  <div className="input-with-button tags-input-row">
                    <input
                      type="text"
                      id="tags"
                      value={pageData.metadata.tags || ''}
                      onChange={(e) => setPageData(prev => ({
                        ...prev,
                        metadata: { ...prev.metadata, tags: e.target.value }
                      }))}
                      className="form-control"
                      placeholder="e.g., book-index, tune-index, scottish, fiddle"
                    />
                    <button
                      type="button"
                      className="generate-button tags-generate-button"
                      onClick={handleAutoPopulateTags}
                      title="Suggest tags from page title, content, metadata, and linked products"
                    >
                      ✨ Auto-Populate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {(pageData.type === 'tune-notation' || pageData.type === 'abc-notation') && !isLegacyPage ? (
          /* Tune pages render from metadata — no HTML editor needed */
          <div className="page-content-editor tune-metadata-notice">
            <h3>🎵 Metadata-Driven Page — No HTML Editor Needed</h3>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
              This tune page is rendered directly from the structured fields above.
              The HTML column is no longer used and does not need to be edited.
            </p>
            <div className="tune-metadata-checklist">
              <div className="checklist-item">
                <span className="checklist-icon">🖼️</span>
                <div>
                  <strong>Notation image</strong>
                  <span> — set via the <em>Notation Asset IDs</em> field in the Assets section above</span>
                </div>
              </div>
              <div className="checklist-item">
                <span className="checklist-icon">🔊</span>
                <div>
                  <strong>Audio player</strong>
                  <span> — set via the <em>Audio Asset IDs</em> field in the Assets section above</span>
                </div>
              </div>
              <div className="checklist-item">
                <span className="checklist-icon">📝</span>
                <div>
                  <strong>About This Tune</strong>
                  <span> — set via the <em>Description</em> field at the top of this page</span>
                </div>
              </div>
              <div className="checklist-item">
                <span className="checklist-icon">📚</span>
                <div>
                  <strong>Featured In (books / recordings)</strong>
                  <span> — populated from <em>product–tune relationships</em> (use the product picker above)</span>
                </div>
              </div>
              <div className="checklist-item">
                <span className="checklist-icon">🎵</span>
                <div>
                  <strong>Related Tunes</strong>
                  <span> — populated automatically from the <em>Tags</em> similarity index</span>
                </div>
              </div>
              <div className="checklist-item">
                <span className="checklist-icon">🔗</span>
                <div>
                  <strong>View Original link</strong>
                  <span> — set via the <em>Original page URL</em> field above</span>
                </div>
              </div>
            </div>
            {pageData.pageId && (
              <p style={{ marginTop: '1.25rem' }}>
                <a
                  href={`/page/${pageData.pageId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline btn-sm"
                >
                  {getIcon('eye')} Preview live page →
                </a>
              </p>
            )}
          </div>
        ) : (
          <div className="page-content-editor">
            <h3>
              Page Content
              <HelpPopup title="How HTML Pages Work in This System" position="right">
                <p><strong>Your content becomes a full SEO-optimized webpage!</strong></p>

                <div className="tip">
                  💡 <strong>What Happens When You Save:</strong><br />
                  Your HTML content is automatically wrapped with:
                  <ul>
                    <li>SEO meta tags (from your Tags field)</li>
                    <li>Page title and description</li>
                    <li>Structured data for search engines</li>
                    <li>Social media sharing tags</li>
                  </ul>
                </div>

                <p><strong>How Tags Connect Your Pages:</strong></p>
                <div className="example-box">
                  <strong>Example:</strong> A tune page with tags "reel, scottish, JSScott"<br />
                  <br />
                  Will automatically show:
                  <ul>
                    <li>Related tunes with matching tags</li>
                    <li>Products tagged with these keywords</li>
                    <li>Other pages in the same categories</li>
                  </ul>
                </div>

                <p><strong>HTML Tips:</strong></p>
                <ul>
                  <li>Use headings (h2, h3) for structure</li>
                  <li>Add images with <code>&lt;img src="..."&gt;</code></li>
                  <li>Link to products: <code>&lt;a href="/product/123"&gt;</code></li>
                  <li>Link to other pages: <code>&lt;a href="/page/page-id"&gt;</code></li>
                </ul>

                <div className="warning">
                  ⚠️ <strong>Rich Text Editor:</strong> The TipTap editor provides visual editing. It generates clean HTML automatically - perfect for non-technical users!
                </div>

                <p><strong>Page URL Structure:</strong></p>
                <div className="example-box">
                  Page ID: <code>winston-fitzgerald-bio</code><br />
                  URL: <code>yoursite.com/page/winston-fitzgerald-bio</code><br />
                  <br />
                  <strong>Tip:</strong> Use descriptive page IDs for better SEO!
                </div>

                <p><strong>Metadata & SEO:</strong></p>
                <p>Fill in all metadata fields above (tags, description, etc.) for best SEO results. Search engines use this info to rank and display your pages!</p>
              </HelpPopup>
            </h3>
            <p className="editor-instructions">
              Use the simple editor for basic HTML or switch to the rich text editor for advanced formatting.
            </p>

            <EditableContentDual
              key={editorKey}
              initialHtml={pageData.html}
              canEdit={true}
              pageId={pageData.pageId}
              pageFlags={{
                title: pageData.title,
                description: pageData.description,
                pageType: pageData.type,
                metadata: pageData.metadata,
                featured: pageData.featured,
                home_announcement: pageData.home_announcement,
                is_hidden: pageData.is_hidden,
              }}
              onContentChange={handleContentChange}
            />
          </div>
        )}

        {!isNewPage && (
          <RelatedContentPreview 
            metadata={pageData.metadata} 
            pageId={pageData.pageId} 
            title={pageData.title}
            description={pageData.description}
            html={pageData.html}
            refreshKey={previewRefreshKey}
            pageType={pageData.type}
          />
        )}

        <div className="page-editor-footer">
          <div className="footer-actions">
            <button
              className="btn btn-secondary"
              onClick={handleBack}
            >
              Back to Pages
            </button>
            <button
              className="btn btn-outline"
              onClick={handlePreview}
            >
              {getIcon('eye')} Preview Page
            </button>

            {!isNewPage && (
              <button
                className="btn btn-outline"
                onClick={toggleVersionHistory}
              >
                {getIcon('clock')} Version History ({versionHistory.length})
              </button>
            )}
          </div>
          {!(pageData.type === 'tune-notation' || pageData.type === 'abc-notation') && (
            <div className="editor-note">
              <p><strong>Note:</strong> Use <strong>Save Page Info</strong> for title, type, pin, and home-page settings (it does not change HTML). Use the editor’s <strong>Save</strong> below to store HTML content.</p>
            </div>
          )}
        </div>

        {/* Version History Panel */}
        {showVersionHistory && (
          <div className="version-history-panel">
            <div className="version-history-header">
              <h3>{getIcon('clock')} Version History</h3>
              <button
                className="btn-close"
                onClick={() => setShowVersionHistory(false)}
              >
                ×
              </button>
            </div>

            {loadingVersions ? (
              <div className="version-loading">Loading version history...</div>
            ) : versionHistory.length === 0 ? (
              <div className="version-empty">No previous versions available</div>
            ) : (
              <div className="version-list">
                {versionHistory.map((version) => (
                  <div key={version.version_id} className="version-item">
                    <div className="version-info">
                      <div className="version-number">Version {version.version_number}</div>
                      <div className="version-title">{version.title}</div>
                      <div className="version-meta">
                        <span className="version-user">{getIcon('user')} {version.changed_by}</span>
                        {version.changed_at && (
                          <span className="version-date">
                            {getIcon('calendar')} {new Date(version.changed_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {version.change_summary && (
                        <div className="version-summary">{version.change_summary}</div>
                      )}
                    </div>
                    <div className="version-actions">
                      {!version.is_legacy && (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => viewVersion(version.version_id)}
                          >
                            {getIcon('eye')} View
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => restoreVersion(version.version_id)}
                          >
                            {getIcon('refresh')} Restore
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Version preview modal */}
        <PagePreviewModal
          isOpen={showVersionPreview}
          onClose={closeVersionPreview}
          versionId={previewVersionId}
        />

        {/* Product Picker Modal — tune related products (multi-select) */}
        <ProductPickerModal
          isOpen={showProductPicker}
          onClose={() => setShowProductPicker(false)}
          multiSelect
          selectedProductIds={selectedProductIds}
          onSelectionChange={handleTuneProductSelectionChange}
          title="📦 Related Products"
        />

        {/* Product Picker Modal — article linked products (multi-select) */}
        <ProductPickerModal
          isOpen={showArticleProductPicker}
          onClose={() => setShowArticleProductPicker(false)}
          multiSelect
          selectedProductIds={selectedProductIds}
          onSelectionChange={handleArticleProductSelectionChange}
          title="📦 Link Products"
        />

      </div>
    </div>
  );
};

export default PageEditor;
